import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ProductGrid,
  CartSidebar,
  PaymentModal,
  ParkBillPanel,
  QuickAddScanner,
  NumPad,
  Button,
  Badge,
  Alert,
  ConfirmDialog,
  Tabs,
  Modal,
  Logo,
  useToast,
  matchActivePath,
} from '@bbpos/ui';
import type { ProductGridItem } from '@bbpos/ui/src/components/POSBillingComponents/ProductGrid/types';
import type { PaymentMethod } from '@bbpos/ui/src/components/POSBillingComponents/PaymentModal/types';
import type { BillMode } from '@bbpos/ui/src/components/POSBillingComponents/BillModeSelector/types';
import type { CustomerLookupResult } from '@bbpos/ui/src/components/POSBillingComponents/CustomerLookup/types';
import type { DenominationRow } from '@bbpos/ui/src/components/POSBillingComponents/DenominationEntry/types';
import type { SplitPayment, CreditNoteLookupResult } from '@bbpos/ui/src/components/POSBillingComponents/PaymentModal/types';
import type { ModifierGroup } from '@bbpos/ui/src/components/RestaurantComponents/ItemModifierPanel/types';
import type { Product, BillPaymentMethod } from '@bbpos/types';
import { useCartStore, applyPromoPreview, selectSupplyOverridePayload, APPROVED_DISCOUNT_APPLIED_BY, RESUMED_DISCOUNT_APPLIED_BY } from '@/stores/use-cart-store';
import { modifierService } from '@/services/modifier.service';
import { formatCurrency } from '@/lib/format';
import { useProducts } from '@/hooks/use-products';
import { useRecipeAvailability } from '@/hooks/use-recipe-availability';
import { menuPeriodService, isPeriodActiveNow, type PosMenuPeriod } from '@/services/menu-period.service';
import { combosService, isComboAvailableNow, type Combo } from '@/services/combos.service';
import { useBillCalculations } from '@/hooks/use-bill-calculations';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { useNetworkStore } from '@/stores/use-network-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import { useTaxInclusive } from '@/hooks/use-tax-inclusive';
import { billingService } from '@/services/billing.service';
import type { BillWithItems } from '@/services/billing.service';
import { vouchersService } from '@/services/vouchers.service';
import { useScopedVoucherRecheck, voucherLinesOf } from '@/hooks/use-scoped-voucher-recheck';
import { productsService, type ExpiredBatchStock, type TrackingMode } from '@/services/products.service';
import { offlineBillingService } from '@/services/offline-billing.service';
import { posDb } from '@/db/pos-database';
import { ApiError } from '@/lib/api-client';
import { ROLE_LEVELS } from '@/lib/quote-constants';
import { useLocationStore } from '@/stores/use-location-store';
import { useShiftStore } from '@/stores/use-shift-store';
import { useRegisterStore } from '@/stores/use-register-store';
import { usePrinterStore } from '@/stores/use-printer-store';
import { useTenantStore } from '@/stores/use-tenant-store';
import { ShiftStatusBadge } from '@/components/features/shifts/ShiftStatusBadge';
import { NetworkStatusBanner } from '@/components/common/NetworkStatusBanner';
import { GstSetupTillBanner } from '@/components/common/GstSetupTillBanner';
import { SUPPLY_DEFAULT_WARNING } from '@/lib/gst-setup';
import { CameraBarcodeScanner } from '@/components/common/CameraBarcodeScanner';
import { BillDiscountModal } from '@/components/common/BillDiscountModal';
import { ShortcutHelpOverlay } from '@/components/common/ShortcutHelpOverlay';
import { NotificationBell } from '@/components/common/NotificationBell';
import { NotificationPanel } from '@/components/common/NotificationPanel';
import { LockMark } from '@/components/common/LockedFeature';
import { usePlanFeatures } from '@/hooks/use-plan-feature';
import { normalizeRole } from '@/lib/discount-caps';
import { useNotificationStore } from '@/stores/use-notification-store';
import { quoteService } from '@/services/quote.service';
import { offlineQuoteService } from '@/services/offline-quote.service';
import { ReorderQRModal } from '@/components/features/ReorderQRModal';
import { PostSaleReceiptModal } from '@/components/features/PostSaleReceiptModal';
import { TerminalPaymentDialog } from '@/components/features/payments/TerminalPaymentDialog';
import { settleOnTerminal, type TerminalWaitState } from '@/components/features/payments/settle-on-terminal';
import {
  isLocationTerminalLinked,
  offlineSlipTender,
  offlineTenderRefusal,
} from '@/lib/offline-tenders';
import { buildReceiptData } from '@/lib/receipt-data';
import { isMeasureUnit, parseQuantityForUnit } from '@/lib/measure-units';
import { cashRoundingForTender, NOT_GST_REGISTERED } from '@/lib/gst';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { CustomerCaptureModal } from '@/components/features/customers/CustomerCaptureModal';
import { SerialCaptureModal } from '@/components/common/SerialCaptureModal';
import { customersService } from '@/services/customers.service';
import { creditAccountService, type CustomerCreditAccount } from '@/services/credit-account.service';
import { ManagerApprovalDialog } from '@/components/common/ManagerApprovalDialog';
import type { ManagerApprovalGrant } from '@/services/manager-approval.service';
import type { LoyaltyBalance } from '@/services/customers.service';
import type { CartSidebarProps } from '@bbpos/ui/src/components/POSBillingComponents/CartSidebar/types';

/** Exactly what CartSidebar wants for the compact 360 card. */
type Customer360Data = NonNullable<CartSidebarProps['customer360']>;
import { useCartPanelPosition } from '@/lib/cart-panel-position';
import { normalizeScannedBarcode } from '@/lib/hardware-scanner';
import { diffDraftLines } from '@/lib/draft-line-diff';
import { toBillItem } from '@/lib/bill-item-payload';
import { comboLineDiscounts } from '@/lib/combo-discount';
import { creditNoteLookupMessage } from '@/lib/credit-note-messages';
import { onSocketEvent } from '@/lib/socket-client';
import { mapVariantsResponse, resolveSelectedProduct } from '@/lib/variant-selection';

// ─── Mobile tab type ─────────────────────────────────────────────────────────
type MobileView = 'products' | 'cart';

// ─── Map UI payment method to API ────────────────────────────────────────────
const PAYMENT_METHOD_MAP: Record<PaymentMethod, BillPaymentMethod | 'SPLIT'> = {
  cash:         'CASH',
  upi:          'UPI',
  card:         'CARD',
  wallet:       'WALLET',
  store_credit: 'STORE_CREDIT',
  // Bearer redemption flows through the same STORE_CREDIT enum on the
  // backend; the credit_note_code/token pair is what distinguishes the path.
  credit_note:  'STORE_CREDIT',
  // Account Credit (ops simulation T08) puts the amount on the customer's
  // linked credit account.
  account_credit: 'CREDIT',
  split:        'SPLIT',
};

/** Billing a quote closes it on the server, so it is never queued offline (T10). */
const QUOTE_BILL_OFFLINE_MSG =
  'Billing a quote needs a connection, so it is closed on every till. Reconnect and try again.';

type CompletePayload = Parameters<typeof billingService.completeBill>[1];

/** Units a cart line takes from one expired batch (QA IS4-004). */
type ExpiredBatchTake = NonNullable<CompletePayload['expired_batch_accepted']>[number];

/** "1 unit from batch B-EXP expired 26 Oct 2026" — the line warning and confirm text. */
function expiredBatchWarning(takes: ExpiredBatchTake[] | undefined): string | undefined {
  if (!takes?.length) return undefined;
  return takes
    .map((t) => {
      // A DATE, not an instant: format it as-is, never shifted by a zone.
      const date = new Date(`${t.expiry_date}T00:00:00Z`).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
      });
      return `${t.quantity} unit${t.quantity === 1 ? '' : 's'} from batch ${t.batch_number} expired ${date}`;
    })
    .join('; ');
}

/**
 * A sale on account over the credit limit, waiting for a manager's PIN (T08).
 * The draft is kept so the same bill completes once the manager approves.
 */
interface PendingCreditApproval {
  billId: string;
  /** Set when this checkout created the draft — voided if the cashier gives up. */
  draftId: string | null;
  payload: CompletePayload;
  method: PaymentMethod;
  reason: string;
  amount: number;
}

/** What a completion puts on account: the whole CREDIT tender, or the CREDIT legs of a split. */
function accountCreditAmount(payload: CompletePayload): number {
  if (payload.payment_method === 'CREDIT') return payload.amount_paid;
  return (payload.payments ?? [])
    .filter((p) => p.method === 'CREDIT')
    .reduce((sum, p) => sum + p.amount, 0);
}

// ─── Role pill (top-bar affordance so cashiers know why Void/Quick-Add hide) ──
// Kept inline because it's trivial and specific to BillingPage; promoting to
// @bbpos/ui would require a shared colour map that doesn't belong there yet.
function roleBadgeStyle(role: string): string {
  switch (role) {
    case 'OWNER':
      return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700/60';
    case 'MANAGER':
      return 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300 border-brand-200 dark:border-brand-700/60';
    case 'CASHIER':
      return 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700';
    case 'SUPER_ADMIN':
    case 'ADMIN':
    case 'MODERATOR':
      return 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300 border-warning-200 dark:border-warning-700/60';
    default:
      return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400 border-neutral-200 dark:border-neutral-700';
  }
}

/** Shown when a resumed draft was completed/voided elsewhere before this checkout. */
const RESUMED_BILL_NOT_DRAFT_MSG =
  'This bill was already completed or voided on another terminal — check Bill History.';

/** Cashier-facing text for the tender refusals the API returns as bare codes. */
/** One line of a 409 PRICE_CHANGED from POST /bills (QA 4.1.4). */
interface PriceChange {
  product_id: string;
  product_name: string;
  expected_unit_price: number;
  current_unit_price: number;
}

function priceChangesOf(err: ApiError): PriceChange[] {
  return (err.details ?? []) as unknown as PriceChange[];
}

const TENDER_REFUSAL_MSG: Record<string, string> = {
  CRM_LOYALTY_INSUFFICIENT:
    "Insufficient wallet balance — the customer's points don't cover this bill. Use Split to pay the rest another way.",
  CRM_LOYALTY_BELOW_MINIMUM:
    'Too few points to redeem — this amount is below the minimum redemption. Choose another payment method.',
  CRM_STORE_CREDIT_INSUFFICIENT:
    "Insufficient store credit — the customer's credit doesn't cover this bill. Use Split to pay the rest another way.",
  // T08: another till used the headroom first; paying again asks for a manager's PIN.
  CREDIT_LIMIT_EXCEEDED:
    "Over the customer's credit limit — pay again to ask a manager to approve, or choose another payment method.",
  // CU2-008: the note was used or expired between the lookup and Confirm.
  CREDIT_NOTE_INVALID_OR_EXPIRED:
    'This credit note is no longer valid — it was used or has expired. Choose another payment method.',
  CREDIT_NOTE_NOT_REDEEMABLE:
    'This credit note can no longer be redeemed — it was used or has expired. Choose another payment method.',
};

/** The routes the header's module buttons open, for the active highlight. */
const HEADER_MODULE_PATHS = [
  '/pos/tables',
  '/pos/products',
  '/pos/inventory/quick-add',
  '/pos/stock-transfer',
  '/pos/bills',
  '/pos/online-orders',
  '/pos/settings',
];

export function BillingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Cart store ───────────────────────────────────────────────────────────
  const isCartHydrated = useCartStore((s) => s.isHydrated);
  const items = useCartStore((s) => s.items);
  const billMode = useCartStore((s) => s.billMode);
  const selectedCustomer = useCartStore((s) => s.selectedCustomer);
  const parkedBills = useCartStore((s) => s.parkedBills);
  const addItem = useCartStore((s) => s.addItem);
  const lastAddedItemId = useCartStore((s) => s.lastAddedItemId);
  const setLineModifiers = useCartStore((s) => s.setLineModifiers);
  const repriceLines = useCartStore((s) => s.repriceLines);
  const removeItem = useCartStore((s) => s.removeItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const setLineSerials = useCartStore((s) => s.setLineSerials);
  const applyItemDiscount = useCartStore((s) => s.applyItemDiscount);
  const clearItemDiscount = useCartStore((s) => s.clearItemDiscount);
  const billDiscount = useCartStore((s) => s.billDiscount);
  const applyBillDiscount = useCartStore((s) => s.applyBillDiscount);
  const clearBillDiscount = useCartStore((s) => s.clearBillDiscount);
  const pendingDiscountApproval = useCartStore((s) => s.pendingDiscountApproval);
  const setPendingDiscountApproval = useCartStore((s) => s.setPendingDiscountApproval);
  const currentBillId = useCartStore((s) => s.currentBillId);
  const setCartBillId = useCartStore((s) => s.setCurrentBillId);
  const membership = useCartStore((s) => s.membership);
  const setMembership = useCartStore((s) => s.setMembership);
  const setCustomer = useCartStore((s) => s.setCustomer);
  const setBillMode = useCartStore((s) => s.setBillMode);
  const clearCart = useCartStore((s) => s.clearCart);
  const parkCurrentBill = useCartStore((s) => s.parkCurrentBill);
  const resumeBill = useCartStore((s) => s.resumeBill);
  const hydrateFromServerBill = useCartStore((s) => s.hydrateFromServerBill);
  const loadQuoteForEdit = useCartStore((s) => s.loadQuoteForEdit);
  const editingQuote = useCartStore((s) => s.editingQuote);
  const loadQuoteForBilling = useCartStore((s) => s.loadQuoteForBilling);
  const billingQuote = useCartStore((s) => s.billingQuote);
  const discardParkedBill = useCartStore((s) => s.discardParkedBill);
  const refreshParkedBills = useCartStore((s) => s.refreshParkedBills);
  const syncParkedBills = useCartStore((s) => s.syncParkedBills);

  // ── Current location (from active shift) ───────────────────────────────
  const currentLocationId = useLocationStore((s) => s.currentLocationId);

  // ── Products ─────────────────────────────────────────────────────────────
  const {
    products,
    gridItems,
    isLoading: productsLoading,
    categories,
    searchQuery: productSearchQuery,
    search: searchProducts,
    clearSearch,
    filterByCategory,
    findByBarcode,
    refresh: refreshProducts,
  } = useProducts({
    // Location comes from the location store inside the hook.
    // Quote mode is the "Sales" surface; everything else is the POS counter.
    channel: billMode === 'quotation' ? 'sales' : 'pos',
  });

  // ── Recipe availability (#308) ───────────────────────────────────────────
  // RESTAURANT only: how many portions of each menu item current ingredient
  // stock can prepare. Feeds the add-to-cart guard in the cart store and the
  // out-of-stock overlay on grid tiles, so a shortage surfaces immediately
  // instead of at payment. No-ops offline (server still validates on
  // completion).
  const isRestaurantTenant = useAuthStore(
    (s) => s.profile?.tenant?.business_type === 'RESTAURANT',
  );
  const { availability: recipeAvailability, refresh: refreshRecipeAvailability } =
    useRecipeAvailability(products.map((p) => p.id), isRestaurantTenant);

  const setIngredientAvailability = useCartStore((s) => s.setIngredientAvailability);
  useEffect(() => {
    setIngredientAvailability(recipeAvailability);
  }, [recipeAvailability, setIngredientAvailability]);

  // ── Dayparts (menu periods) ──────────────────────────────────────────────
  // Restrict the catalog to items orderable in the selected daypart. Defaults
  // to whichever period is active right now; the cashier/manager can switch.
  const [periods, setPeriods] = useState<PosMenuPeriod[]>([]);
  const [selectedDaypartId, setSelectedDaypartId] = useState<string>('__all');

  useEffect(() => {
    if (!currentLocationId) { setPeriods([]); return; }
    menuPeriodService.list(currentLocationId)
      .then((res) => {
        const list = (res.data ?? []).filter((p) => p.is_active !== false);
        setPeriods(list);
        const active = list.find((p) => isPeriodActiveNow(p));
        setSelectedDaypartId(active ? active.id : '__all');
      })
      .catch(() => setPeriods([]));
  }, [currentLocationId]);

  const daypartGridItems = useMemo(() => {
    if (selectedDaypartId === '__all') return gridItems;
    const period = periods.find((p) => p.id === selectedDaypartId);
    if (!period) return gridItems;
    const allowed = new Set(period.product_ids);
    return gridItems.filter((g) => allowed.has(g.id));
  }, [gridItems, selectedDaypartId, periods]);

  // ── Combos (meal deals) ───────────────────────────────────────────────────
  // Combos live in a separate table and are tapped to expand into their
  // component dishes (with a discount applied so the total = combo price).
  const [combos, setCombos] = useState<Combo[]>([]);

  // Re-poll every 60s so a combo deactivated in Admin (server drops it from the
  // active list) or whose schedule window has closed disappears from the POS
  // automatically, without a manual refresh.
  useEffect(() => {
    if (!currentLocationId) { setCombos([]); return; }
    setCombos([]); // reset when switching location
    let cancelled = false;
    const load = () => {
      combosService.list({ location_id: currentLocationId, status: 'active' })
        .then((res) => {
          if (!cancelled) setCombos((res.data ?? []).filter((c) => c.is_active && c.items.length > 0));
        })
        .catch(() => { /* keep last-known on a transient/offline failure */ });
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [currentLocationId]);

  // ── Add-on (modifier) panel ───────────────────────────────────────────────
  // When a product with modifier groups is added, open the panel so the cashier
  // can pick add-ons; the chosen surcharge is folded into the line's unit price
  // so it reaches the bill total. Products with no groups add straight to cart.
  const [locationModifiers, setLocationModifiers] = useState<
    { group: ModifierGroup; productIds: string[] }[]
  >([]);
  const [pendingModifier, setPendingModifier] = useState<{
    lineId: string;
    productName: string;
    basePrice: number;
    groups: ModifierGroup[];
  } | null>(null);
  const [selectedModifiers, setSelectedModifiers] = useState<Record<string, string[]>>({});
  const [editedPrices, setEditedPrices] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!currentLocationId) { setLocationModifiers([]); return; }
    let cancelled = false;
    modifierService
      .getForLocation(currentLocationId)
      .then((entries) => { if (!cancelled) setLocationModifiers(entries); })
      .catch(() => { if (!cancelled) setLocationModifiers([]); });
    return () => { cancelled = true; };
  }, [currentLocationId]);

  const groupsForProduct = useCallback(
    (productId: string) =>
      locationModifiers
        .filter((e) => e.productIds.length === 0 || e.productIds.includes(productId))
        .map((e) => e.group),
    [locationModifiers],
  );

  // 1-minute tick so time-window expiry is re-evaluated even between re-polls.
  const [comboClock, setComboClock] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setComboClock((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  /**
   * Loads a parent product's variants for the picker. The server returns the
   * attribute groups (ordered by the tenant's attribute library) and the child
   * rows; the children are kept so a picked variant can be added to the cart
   * as itself (its own id / sku / price / tax) — the grid only holds parents.
   */
  const loadedVariantsRef = useRef(new Map<string, Product>());
  const loadVariants = useCallback(async (productId: string) => {
    const channel = billMode === 'quotation' ? 'sales' : 'pos';
    const picker = mapVariantsResponse(await productsService.getVariants(productId, channel));
    for (const [id, child] of picker.children) loadedVariantsRef.current.set(id, child);
    return { attributes: picker.attributes, variants: picker.variants };
  }, [billMode]);

  // Combo tiles are prepended to the grid (independent of daypart). Only combos
  // that are available *right now* (active + within their schedule) are shown.
  const comboGridItems = useMemo<ProductGridItem[]>(
    () => {
      void comboClock; // re-evaluate availability on each minute tick
      return combos
        .filter((c) => isComboAvailableNow(c))
        .map((c) => ({
          id: `combo:${c.id}`,
          name: c.name,
          sku: 'COMBO',
          price: c.combo_price,
          stock: 1, // not stock-tracked; keep > 0 so it isn't treated as out-of-stock
          hasVariants: false,
          isCombo: true,
        }));
    },
    [combos, comboClock],
  );

  const gridWithCombos = useMemo(
    () => [...comboGridItems, ...daypartGridItems],
    [comboGridItems, daypartGridItems],
  );

  // #308: menu items whose recipe can't produce even one portion render as
  // out-of-stock tiles — the grid greys them out and offers alternatives
  // instead of adding them to the cart.
  const gridWithAvailability = useMemo(
    () =>
      gridWithCombos.map((g) => {
        const a = recipeAvailability[g.id];
        return a && a.available <= 0 ? { ...g, stock: 0, isLowStock: false } : g;
      }),
    [gridWithCombos, recipeAvailability],
  );

  // ── Bill calculations (incl. pro-rated bill-level discount) ─────────────
  // Owner display setting (tenant-wide): show tile/cart prices inclusive of
  // tax. Presentation only — totals are base + tax either way.
  const taxInclusivePricing = useTaxInclusive();
  const isInterState = useCartStore((s) => s.isInterState);
  const setIsInterState = useCartStore((s) => s.setIsInterState);
  // Promotional rules are applied by the server at checkout; preview them so
  // the cart shows the total it will actually charge (QA 4.3.16). The answer
  // is kept with the cart snapshot it was for and ignored once that changes.
  const isOnline = useNetworkStore((s) => s.isConnected);
  // The attached customer is part of the key: segment rules depend on it.
  const promoCustomerId = selectedCustomer?.id;
  const promoKey = `${promoCustomerId ?? ''}#${items
    .map((i) => `${i.id}:${i.productId}:${i.quantity}:${i.unitPrice}:${i.discountAmount}`)
    .join('|')}`;
  const [promoPreview, setPromoPreview] = useState<{ key: string; byLine: Record<string, number> }>({
    key: '',
    byLine: {},
  });
  useEffect(() => {
    if (!isOnline || items.length === 0) return;
    const snapshot = items;
    const key = promoKey;
    const timer = setTimeout(() => {
      billingService
        .previewPromotions(
          snapshot.map((i) => ({
            product_id: i.productId,
            quantity: i.quantity,
            unit_price: i.unitPrice,
            discount: i.discountAmount,
          })),
          promoCustomerId,
        )
        .then((res) => {
          const byLine: Record<string, number> = {};
          (res?.lines ?? []).forEach((l, idx) => {
            if (l.discount > 0 && snapshot[idx]) byLine[snapshot[idx].id] = l.discount;
          });
          setPromoPreview({ key, byLine });
        })
        .catch(() => {
          /* preview only — the server still prices the sale */
        });
    }, 250);
    return () => clearTimeout(timer);
    // `items` is captured through promoKey, which changes whenever a priced field does.
  }, [promoKey, isOnline]);
  const pricedItems = useMemo(
    () =>
      promoPreview.key === promoKey
        ? applyPromoPreview(items, promoPreview.byLine, isInterState)
        : items,
    [items, promoPreview, promoKey, isInterState],
  );
  // A resumed EXCHANGE draft: the return's credit is applied by the server
  // first, so the till collects only the difference (QA 4.5.6).
  const exchangeCredit = useCartStore((s) => s.exchangeCredit);
  // T10: a billed quote's down payment is already paid; the server deducts it.
  const quoteAdvance = billingQuote?.advancePaid ?? 0;
  // Cash rounding applies to the difference, not the whole bill (PS2-001).
  const totals = useBillCalculations(
    pricedItems, billDiscount, isInterState, (exchangeCredit?.amount ?? 0) + quoteAdvance,
  );
  // Capped at the unrounded total, as the server caps it.
  const exchangeApplied = Math.min(exchangeCredit?.amount ?? 0, totals.grandTotal - totals.roundOff);
  const payableTotal = totals.grandTotal - exchangeApplied - quoteAdvance;

  // ── Network status ─────────────────────────────────────────────────────
  const isConnected = useNetworkStore((s) => s.isConnected);

  // ── Serial-tracked lines ───────────────────────────────────────────────
  const [showSerialCapture, setShowSerialCapture] = useState(false);
  const [trackingModes, setTrackingModes] = useState<Record<string, TrackingMode>>({});
  // Which cart (product set + connectivity) `trackingModes` was resolved for.
  // Until it matches the current cart the Pay gate can't tell whether a line
  // needs serials, so a Pay click waits for the lookup (QA 3.9.10).
  const [trackingModesFor, setTrackingModesFor] = useState('');
  // Resolve tracking mode for the distinct products in the cart. Only refetched
  // when the set of products changes, not on every quantity tweak.
  const productIdsKey = useMemo(
    () => [...new Set(items.map((i) => i.productId))].sort().join(','),
    [items],
  );
  const trackingModesKey = `${isConnected ? 'on' : 'off'}:${productIdsKey}`;
  const trackingModesPending = trackingModesFor !== trackingModesKey;
  useEffect(() => {
    const ids = productIdsKey ? productIdsKey.split(',') : [];
    if (ids.length === 0 || !isConnected) {
      setTrackingModes({});
      setTrackingModesFor(trackingModesKey);
      return;
    }
    let cancelled = false;
    productsService
      .getTrackingModes(ids)
      .then((modes) => {
        if (!cancelled) setTrackingModes(modes);
      })
      // A failed lookup must not block selling. The server still enforces
      // serials at completion, so the worst case is the error surfacing there.
      .catch(() => {
        if (!cancelled) setTrackingModes({});
      })
      .finally(() => {
        if (!cancelled) setTrackingModesFor(trackingModesKey);
      });
    return () => {
      cancelled = true;
    };
  }, [productIdsKey, isConnected, trackingModesKey]);

  const serialLines = useMemo(
    () =>
      items
        .filter((i) => trackingModes[i.productId] === 'SERIAL')
        .map((i) => ({
          id: i.id,
          productId: i.productId,
          name: i.name,
          sku: i.sku,
          quantity: i.quantity,
          serials: i.serials ?? [],
        })),
    [items, trackingModes],
  );
  const serialsOutstanding = useMemo(
    () => serialLines.some((l) => l.serials.length !== l.quantity),
    [serialLines],
  );

  // ── Expired batches (QA IS4-004, owner decision ID13: warn but allow) ──
  // A sale takes unexpired batches first and falls back to expired ones once
  // they run out. The till warns on the line and asks before payment; the
  // acceptance goes on the completion request, which the audit log keeps.
  const [expiredBatchStock, setExpiredBatchStock] = useState<Record<string, ExpiredBatchStock>>({});
  const [expiredBatchStockFor, setExpiredBatchStockFor] = useState('');
  const expiredBatchStockKey = `${trackingModesKey}:${currentLocationId ?? ''}`;
  const expiredBatchStockPending = expiredBatchStockFor !== expiredBatchStockKey;
  useEffect(() => {
    const ids = productIdsKey ? productIdsKey.split(',') : [];
    if (ids.length === 0 || !isConnected || !currentLocationId) {
      setExpiredBatchStock({});
      setExpiredBatchStockFor(expiredBatchStockKey);
      return;
    }
    let cancelled = false;
    productsService
      .getExpiredBatchStock(ids, currentLocationId)
      .then((stock) => {
        if (!cancelled) setExpiredBatchStock(stock);
      })
      // A failed lookup must not block selling; the warning is advisory.
      .catch(() => {
        if (!cancelled) setExpiredBatchStock({});
      })
      .finally(() => {
        if (!cancelled) setExpiredBatchStockFor(expiredBatchStockKey);
      });
    return () => {
      cancelled = true;
    };
  }, [productIdsKey, isConnected, currentLocationId, expiredBatchStockKey]);

  /** Units each product's line will take from expired batches, in consumption order. */
  const expiredBatchTakes = useMemo(() => {
    const qtyByProduct = new Map<string, number>();
    for (const i of items) qtyByProduct.set(i.productId, (qtyByProduct.get(i.productId) ?? 0) + i.quantity);
    const out = new Map<string, ExpiredBatchTake[]>();
    for (const [productId, qty] of qtyByProduct) {
      const stock = expiredBatchStock[productId];
      if (!stock) continue;
      let left = Math.round((qty - stock.unexpired_quantity) * 1000) / 1000;
      const takes: ExpiredBatchTake[] = [];
      for (const b of stock.expired_batches) {
        if (left <= 0) break;
        const take = Math.min(b.quantity, left);
        takes.push({ product_id: productId, batch_number: b.batch_number, expiry_date: b.expiry_date, quantity: take });
        left = Math.round((left - take) * 1000) / 1000;
      }
      if (takes.length > 0) out.set(productId, takes);
    }
    return out;
  }, [items, expiredBatchStock]);
  const expiredBatchAccepted = useMemo(() => [...expiredBatchTakes.values()].flat(), [expiredBatchTakes]);
  // What the cashier accepted; a cart change that alters the takes asks again.
  const expiredBatchAcceptKey = JSON.stringify(expiredBatchAccepted);
  const [expiredBatchAcceptedKey, setExpiredBatchAcceptedKey] = useState<string | null>(null);
  const [showExpiredBatchConfirm, setShowExpiredBatchConfirm] = useState(false);

  // ── Auto-apply membership tier discount ─────────────────────────────────
  // When a customer with an active membership is attached, mirror the backend's
  // createBill auto-apply: pull the tier discount and let the cart store apply
  // it per line. Keyed on the customer id so it also covers resumed bills.
  const customerId = selectedCustomer?.id;
  useEffect(() => {
    if (!customerId || !isConnected) {
      setMembership(null);
      return;
    }
    let cancelled = false;
    customersService
      .getActiveMembership(customerId)
      .then((m) => {
        if (cancelled) return;
        setMembership(
          m
            ? { planName: m.plan_name, tier: m.tier_level, discountPct: m.discount_percentage }
            : null,
        );
      })
      .catch(() => {
        // 404/offline/no-membership — fail closed (no auto discount).
        if (!cancelled) setMembership(null);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, isConnected, setMembership]);

  // ── Loyalty balance + the tenant's redemption rules ─────────────────────
  // Fetched per attached customer, the same shape as the membership effect
  // above. Feeds the points control on the payment screen; absent (walk-in,
  // offline, or a tenant with the programme switched off) the wallet tender
  // behaves exactly as it did before — settle the whole bill.
  const [loyaltyBalance, setLoyaltyBalance] = useState<LoyaltyBalance | null>(null);
  useEffect(() => {
    if (!customerId || !isConnected) {
      setLoyaltyBalance(null);
      return;
    }
    let cancelled = false;
    customersService
      .getLoyaltyBalance(customerId)
      .then((b) => {
        if (!cancelled) setLoyaltyBalance(b);
      })
      .catch(() => {
        // A failed lookup must not block selling — the server re-checks the
        // balance and the floor on completion regardless.
        if (!cancelled) setLoyaltyBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, isConnected]);

  // Account Credit (T08): the customer's linked credit account. Any failure
  // (none, credit billing off or not on the plan → 403) means "not offered".
  const [creditAccount, setCreditAccount] = useState<CustomerCreditAccount | null>(null);
  useEffect(() => {
    if (!customerId || !isConnected) {
      setCreditAccount(null);
      return;
    }
    let cancelled = false;
    creditAccountService
      .getForCustomer(customerId)
      .then((a) => {
        if (!cancelled) setCreditAccount(a ?? null);
      })
      .catch(() => {
        if (!cancelled) setCreditAccount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, isConnected]);
  const [pendingCreditApproval, setPendingCreditApproval] = useState<PendingCreditApproval | null>(null);

  /**
   * What the payment screen needs to offer a partial redemption.
   *
   * Null unless the programme is ACTIVE and the customer actually has points:
   * a points slider on a zero balance is a control that can only disappoint.
   */
  const loyaltyContext = useMemo(
    () =>
      loyaltyBalance && loyaltyBalance.program_active && loyaltyBalance.loyalty_points > 0
        ? {
            availablePoints: loyaltyBalance.loyalty_points,
            pointValuePaise: loyaltyBalance.redemption_value_paise,
            minimumPoints: loyaltyBalance.min_redemption_points,
          }
        : undefined,
    [loyaltyBalance],
  );

  // ── Customer 360, at the counter ────────────────────────────────────────
  // The compact card has existed in @bbpos/ui since the Tier-4 batch and was
  // never mounted, so a cashier attaching a customer saw a name, a phone and a
  // points count and nothing about who they were dealing with.
  //
  // allSettled, not all: the RFM and identity endpoints are secondary, and a
  // 404 from either must not blank the lifetime spend, which is the number the
  // cashier is actually looking at. One round of three parallel requests per
  // customer attach, keyed on the id like the membership lookup above.
  const [customer360, setCustomer360] = useState<Customer360Data | null>(null);
  useEffect(() => {
    if (!customerId || !isConnected) {
      setCustomer360(null);
      return;
    }
    let cancelled = false;
    void Promise.allSettled([
      customersService.getById(customerId),
      customersService.getRfm(customerId),
      customersService.getIdentityScore(customerId),
    ]).then(([detailRes, rfmRes, identityRes]) => {
      if (cancelled) return;
      if (detailRes.status !== 'fulfilled') {
        setCustomer360(null);
        return;
      }
      const d = detailRes.value;
      setCustomer360({
        customer: { id: d.id, name: d.name, phone: d.phone, email: d.email ?? undefined },
        stats: {
          lifetimeSpend: d.total_spent,
          visitCount: d.visit_count,
          avgBillValue: d.avg_order_value,
          // The card types this as a Date. No visit yet reads as the epoch,
          // which the compact view renders as "—" rather than inventing one.
          lastVisit: d.last_visit_at ? new Date(d.last_visit_at) : new Date(0),
        },
        loyaltyBalance: d.loyalty_points,
        storeCredit: {
          outstanding: d.store_credit_balance,
          // Store credit here is money the shop OWES the customer, so it is
          // never overdue. The card's overdue banner is for the credit-ledger
          // sense of the word, which this number is not.
          overdue: false,
        },
        rfmTag: rfmRes.status === 'fulfilled' ? rfmRes.value.segment : undefined,
        identityScore:
          identityRes.status === 'fulfilled' ? identityRes.value.identity_score : undefined,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [customerId, isConnected]);

  // ── User profile (for offline location resolution) ─────────────────────
  const profile = useAuthStore((s) => s.profile);
  // Header buttons whose feature is outside the plan stay clickable but show a
  // lock; the route itself explains (plan §7).
  const hasPlanFeature = usePlanFeatures();
  // `!` beats the ghost variant's own text colour (Button concatenates classes).
  const lockedButtonClass = '!text-neutral-400 dark:!text-neutral-500';
  // Header module buttons light the one the current route belongs to, by the
  // same best-match rule as the bottom nav (/pos/bills/<id> → Bills).
  const { pathname } = useLocation();
  const activeModule = matchActivePath(HEADER_MODULE_PATHS, pathname);
  const moduleButtonClass = (path: string, extra?: string) =>
    [
      activeModule === path
        ? '!bg-brand-50 !text-brand-700 font-semibold dark:!bg-brand-900/30 dark:!text-brand-300'
        : undefined,
      extra,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  // Permissions honouring tenant policy. Used both to filter the bill-mode
  // pills (Return appears only when the user can issue credit notes) and to
  // gate the corresponding actions later in the flow.
  const modePermissions = {
    can_issue_credit_note: profile?.permissions?.can_issue_credit_note ?? false,
  };

  // Auto-fallback: if the user was sitting in Return mode and the
  // permission disappears (e.g. owner restricted it mid-shift), reset to
  // Sale so the cashier doesn't end up with a hidden pill they can't switch
  // away from. Cheap effect — runs only when one of these inputs changes.
  useEffect(() => {
    if (billMode === 'return' && !modePermissions.can_issue_credit_note) {
      setBillMode('sale');
    }
  }, [billMode, modePermissions.can_issue_credit_note, setBillMode]);

  // Quote mode needs the plan's quotations feature (PS1-003): the pill shows a
  // lock, and a quote mode left over from an earlier session falls back to Sale
  // so no quote is built — or queued offline — that the API would refuse.
  const quotesLocked = !hasPlanFeature('quotations');
  useEffect(() => {
    if (billMode === 'quotation' && quotesLocked) {
      setBillMode('sale');
    }
  }, [billMode, quotesLocked, setBillMode]);

  // T09 — 'Edit taxes': the bill-level IGST switch is a tax change, so a user
  // without the permission (a cashier) needs a manager's PIN to flip it. The
  // value waits here while the manager approves; the approval is logged, and
  // its token goes with the bill so the server keeps the switch (gst-parked D7).
  const canEditTaxes = profile?.permissions?.can_edit_taxes ?? false;
  // The store's split is only the same-state default until a GSTIN / state is set (D4).
  const supplySource = profile?.locations?.find((l) => l.id === currentLocationId)?.supply_source;
  const [pendingInterState, setPendingInterState] = useState<boolean | null>(null);
  const handleInterStateChange = useCallback(
    (value: boolean) => {
      if (canEditTaxes) setIsInterState(value);
      else setPendingInterState(value);
    },
    [canEditTaxes, setIsInterState],
  );
  // Per-user POS layout pref — flips cart sidebar to screen-left when LEFT.
  // "Left"/"Right" mean literal screen-side and do NOT flip with dir="rtl".
  const [cartPanelPosition] = useCartPanelPosition(profile?.id);
  // Normalize once — defensive against lowercase roles from the auth store,
  // and fed straight to BillDiscountModal + the role badge so they stay in
  // sync with the gating in ROLE_LEVELS.
  const normalizedRole = normalizeRole(profile?.role ?? null);

  // ── Tenant currency (immutable per tenant — see use-tenant-currency) ────
  const tenantCurrency = useTenantCurrency();
  const tax = useTaxRegime();
  // gst-parked 090 (D5): not GST registered — no GST on the cart (lib/gst.ts
  // zeroes it), no IGST switch, no tax-inclusive prices; the shelf price is
  // the price. India only (a VAT tenant's `gst_registered` is always false).
  const noGst = tax.regime === 'GST_INDIA' && profile?.tenant?.gst_registered === false;

  // ── Modal state ──────────────────────────────────────────────────────────
  const [showPayment, setShowPayment] = useState(false);
  // Serial capture: which cart products are SERIAL-tracked, and whether the
  // capture step is open. Modes are fetched per distinct product in the cart.
  const [showCustomerCapture, setShowCustomerCapture] = useState(false);
  // When the cashier explicitly picks "Skip — walk-in customer" for the current
  // draft, bypass the capture modal on subsequent Pay clicks. Resets when the
  // cart clears (new bill) or a customer is later attached via the sidebar.
  const [customerSkipped, setCustomerSkipped] = useState(false);
  const [showParkPanel, setShowParkPanel] = useState(false);
  // gst-parked 120: a park is a server round trip — one at a time.
  const [isParking, setIsParking] = useState(false);
  const [showBillDiscountModal, setShowBillDiscountModal] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  // QA 4.4.12: reason kept on a server draft when it is voided.
  const [voidReason, setVoidReason] = useState('');
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Card/UPI: the amount pushed to the terminal, shown while the till waits.
  const [terminalWait, setTerminalWait] = useState<TerminalWaitState | null>(null);
  const terminalAbortRef = useRef<AbortController | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Id of the quote just created, so the success banner can offer a way to
  // open it. Without this the cart clears and the new quote is unreachable
  // from this screen — the number in the message is not a link.
  const [createdQuoteId, setCreatedQuoteId] = useState<string | null>(null);
  // One auto-hide timer for the success banner: a new message cancels the
  // previous one's timer, which would otherwise hide the new message early.
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideSuccessAfter = useCallback((ms: number) => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => {
      successTimerRef.current = null;
      setSuccessMsg(null);
      setCreatedQuoteId(null);
    }, ms);
  }, []);

  // #308: an error banner (e.g. "Insufficient ingredient …" from a failed
  // payment) is stale the moment the cart changes — clear it so it doesn't
  // linger while the cashier removes the offending item, and so a fresh
  // failure on the next Pay attempt re-surfaces cleanly.
  useEffect(() => {
    setError(null);
  }, [items]);

  // QA 4.1.4: a "price changed since added" notice. Kept apart from `error`,
  // which the effect above clears on every cart change — and re-pricing the
  // cart IS a cart change. Cleared on dismiss or on the next payment attempt.
  const [priceNotice, setPriceNotice] = useState<string | null>(null);

  // ── Re-order QR modal (G4.3) ────────────────────────────────────────────
  // The settled bill, shown as a receipt the moment payment succeeds. Holds the
  // `completeBill` response so the figures survive clearCart() wiping the store.
  const [receiptBill, setReceiptBill] = useState<BillWithItems | null>(null);
  // T19: the receipt is of a bill saved offline (OFL number, no server copy yet).
  const [receiptOffline, setReceiptOffline] = useState(false);
  // T19: an offline card/UPI sale waits here for the cashier to confirm the slip.
  const [offlineSlip, setOfflineSlip] = useState<{
    method: PaymentMethod;
    splits?: SplitPayment[];
    cashTendered?: number;
    methods: Array<'card' | 'upi'>;
    amount: number;
  } | null>(null);
  const [offlineSlipReference, setOfflineSlipReference] = useState('');
  // Set by the slip dialog's Confirm for the one checkout it resumes.
  const offlineSlipConfirmedRef = useRef<{ reference: string } | null>(null);

  // Opened after a successful bill completion so the cashier can either hand
  // the customer a QR to scan on their phone or fire a WhatsApp/Email link.
  const [reorderModalBill, setReorderModalBill] = useState<{
    billId: string;
    billNumber: string;
    phone: string | null;
    email: string | null;
  } | null>(null);
  // Queued while the receipt is on screen — both modals are `fixed inset-0
  // z-50`, so the re-order QR is promoted only once the receipt is dismissed.
  const [pendingReorderBill, setPendingReorderBill] = useState<{
    billId: string;
    billNumber: string;
    phone: string | null;
    email: string | null;
  } | null>(null);

  // ── F10 imperative handle — PaymentModal writes its submit fn here so
  //    the F10 shortcut can trigger Confirm Payment without lifting state.
  //    Returns true if the submit actually fired (canConfirm was true).
  const paymentConfirmTriggerRef = useRef<(() => boolean) | null>(null);

  // ── Mobile view toggle ───────────────────────────────────────────────────
  const [mobileView, setMobileView] = useState<MobileView>('products');

  // ── Camera barcode scanner ──────────────────────────────────────────────
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const cameraSupported = typeof navigator !== 'undefined'
    && 'mediaDevices' in navigator
    && typeof navigator.mediaDevices?.getUserMedia === 'function';

  // ── Scanner preferences (from Settings page, persisted in localStorage) ──
  const [scannerPrefs, setScannerPrefs] = useState({
    preferredCameraId: '',
    soundOnScan: true,
    continuousScanMode: false,
    hardwareScannerEnabled: true,
    scannerPrefix: '',
    scannerSuffix: '',
    minBarcodeLength: 3,
  });
  // ── Terminal preferences (from Settings page, persisted in localStorage) ─
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<PaymentMethod>('cash');
  const [quickAddQuantity, setQuickAddQuantity] = useState(true);
  const [pendingQtyProduct, setPendingQtyProduct] = useState<Product | null>(null);
  const [qtyInput, setQtyInput] = useState('1');

  useEffect(() => {
    try {
      const raw = localStorage.getItem('bbpos_pos_prefs');
      if (raw) {
        const prefs = JSON.parse(raw);
        setScannerPrefs({
          preferredCameraId: prefs.preferredCameraId ?? '',
          soundOnScan: prefs.soundOnScan ?? true,
          continuousScanMode: prefs.continuousScanMode ?? false,
          hardwareScannerEnabled: prefs.hardwareScannerEnabled ?? true,
          scannerPrefix: prefs.scannerPrefix ?? '',
          scannerSuffix: prefs.scannerSuffix ?? '',
          minBarcodeLength: Number(prefs.minBarcodeLength) > 0 ? Number(prefs.minBarcodeLength) : 3,
        });
        setQuickAddQuantity(prefs.quickAddQuantity ?? true);
        // Settings stores the API-shaped value ('CASH'); the modal speaks lowercase.
        const method = String(prefs.defaultPaymentMethod ?? '').toLowerCase();
        if (method === 'cash' || method === 'upi' || method === 'card') {
          setDefaultPaymentMethod(method);
        }
      }
    } catch { /* ignore */ }
  }, []);

  const { toast } = useToast();
  const isOwner = profile?.role === 'OWNER';
  const showQuotesLockedToast = useCallback(() => {
    toast({
      title: 'Quotes are not in your plan',
      description: isOwner
        ? 'Upgrade your plan in My Subscription to save quotes.'
        : 'Ask your owner to upgrade.',
      variant: 'warning',
    });
  }, [toast, isOwner]);
  const ensureActiveShift = useShiftStore((s) => s.ensureActiveShift);
  // Sale / Credit / Return take or pay out money through a drawer, which
  // belongs to a shift: send the cashier to open one first rather than billing
  // against none (FR-STAFF-001, PAGES.md Open Shift). Quotes move no money.
  const ensureRegisterReady = useRegisterStore((s) => s.ensureReady);
  const requireShift = useCallback(async (): Promise<boolean> => {
    if (!(await ensureActiveShift())) {
      toast({ title: 'Open a shift first', description: 'Clock in to a shift before billing.', variant: 'warning' });
      navigate('/pos/shift/open');
      return false;
    }
    // T12: where the store has registers, money goes through an open register.
    const locationId = useLocationStore.getState().currentLocationId;
    if (!locationId) return true;
    const readiness = await ensureRegisterReady(locationId);
    if (readiness === 'ready' || readiness === 'no-registers') return true;
    toast({
      title: readiness === 'needs-register' ? "Pick this till's register" : 'Open the register first',
      description: 'Bills are recorded on the register session.',
      variant: 'warning',
    });
    navigate('/pos/register');
    return false;
  }, [ensureActiveShift, ensureRegisterReady, toast, navigate]);

  // ── Category filter ──────────────────────────────────────────────────────
  const [activeCategory, setActiveCategory] = useState<string | undefined>();

  // ── Notification polling ─────────────────────────────────────────────────
  const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount);
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // ── Search ref for keyboard shortcut ─────────────────────────────────────
  const scannerRef = useRef<HTMLDivElement>(null);
  // `proceedToPayment` is defined further down (it depends on cart-derived
  // state); the F-key handler reaches it through this ref so both entry points
  // share one gate without reordering the component.
  const proceedToPaymentRef = useRef<() => void>(() => undefined);

  // ── Keyboard shortcuts (PAGES.md §2.2) ───────────────────────────────────
  useKeyboardShortcuts({
    onToggleScanner: () => setShowCameraScanner((prev) => !prev),
    onParkBill: () => setShowParkPanel(true),
    onOpenPayment: () => {
      // Routed through the same gate as the Pay button so the shortcut can't
      // skip serial capture or the open-shift check.
      if (!(items.length > 0 && currentLocationId)) return;
      if (billMode === 'quotation') {
        proceedToPaymentRef.current();
        return;
      }
      void requireShift().then((ok) => {
        if (ok) proceedToPaymentRef.current();
      });
    },
    onCloseModal: () => {
      // Precedence: close the innermost/most-transient modal first.
      if (showShortcutHelp) setShowShortcutHelp(false);
      else if (showVoidConfirm) setShowVoidConfirm(false);
      else if (showBillDiscountModal) setShowBillDiscountModal(false);
      else if (showCameraScanner) setShowCameraScanner(false);
      else if (showPayment) setShowPayment(false);
      else if (showParkPanel) setShowParkPanel(false);
    },
    onFocusSearch: () => {
      const input = scannerRef.current?.querySelector('input');
      input?.focus();
    },
    // ── F8: open bill-discount modal ────────────────────────────────────
    onApplyBillDiscount: () => {
      if (items.length === 0) {
        toast({ title: 'Add items to the cart first.', variant: 'warning' });
        return;
      }
      if (showPayment || showParkPanel || showCameraScanner || showVoidConfirm) {
        return;
      }
      setShowBillDiscountModal(true);
    },
    // ── F10: complete payment (only fires when payment modal is open) ──
    onCompletePayment: () => {
      if (!showPayment) return;
      const trigger = paymentConfirmTriggerRef.current;
      if (!trigger) return;
      const fired = trigger();
      if (!fired) {
        toast({
          title: 'Cannot complete yet — check payment details.',
          variant: 'warning',
        });
      }
    },
    // ── F12: void current bill (MANAGER+ only) ─────────────────────────
    onVoidBill: () => {
      if (items.length === 0) return;
      const level = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
      if (level < ROLE_LEVELS.MANAGER) {
        toast({ title: 'Void requires manager approval.', variant: 'warning' });
        return;
      }
      setShowVoidConfirm(true);
    },
    // ── Ctrl+Z: undo last added cart item ──────────────────────────────
    onUndoLastItem: () => {
      if (items.length === 0) return;
      const last = items[items.length - 1];
      removeItem(last.id);
      toast({ title: `Removed ${last.name}`, variant: 'info' });
    },
    // ── ? : show shortcut help overlay ──────────────────────────────────
    onShowHelp: () => setShowShortcutHelp(true),
  });

  // ── Resume a bill kept on the server ──────────────────────────────────────
  //
  // gst-parked 120 (D1 / D2): a PARKED bill is the store's — any cashier at
  // the location may resume it, on any till. It is turned back into a DRAFT
  // (which claims the draft lock) and loaded; a DRAFT from Bill History only
  // needs its lock. Either way checkout then completes that same bill.
  // `parkedOnly` (the Parked drawer, QA GP2-003): a bill that is no longer
  // PARKED is open on another till or closed — refuse it, never take the
  // DRAFT path that Bill History uses.
  const resumeServerBill = useCallback(
    async (billId: string, opts: { parkedOnly?: boolean } = {}) => {
      const canTakeOver = (ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.MANAGER;
      try {
        let bill = await billingService.getBill(billId);
        if (currentLocationId && bill.location_id !== currentLocationId) {
          toast({ title: 'This bill belongs to another store — switch location to resume it.', variant: 'error' });
          return;
        }
        if (opts.parkedOnly && (bill.status as string) !== 'PARKED') {
          toast({ title: 'This bill is no longer parked — another till has it or it was closed.', variant: 'error' });
          void refreshParkedBills();
          return;
        }
        // The API returns 'PARKED' here, though the shared BillStatus type omits it.
        if ((bill.status as string) === 'PARKED') {
          try {
            await billingService.resumeBill(billId);
          } catch (resumeErr) {
            if (!(resumeErr instanceof ApiError) || resumeErr.statusCode !== 409) throw resumeErr;
            const locked =
              (resumeErr.body as { error?: string } | undefined)?.error === 'BILL_LOCKED' ||
              /another till|being edited by/i.test(resumeErr.message);
            if (!locked) {
              // Resumed, completed or voided on another till a moment ago.
              toast({ title: 'This bill is no longer parked — another till has it or it was closed.', variant: 'error' });
              void refreshParkedBills();
              return;
            }
            if (canTakeOver && window.confirm(`${resumeErr.message}.\nTake over this bill?`)) {
              await billingService.resumeBill(billId, true);
            } else {
              toast({ title: resumeErr.message, variant: 'error' });
              return;
            }
          }
          bill = await billingService.getBill(billId);
        }
        if (bill.status !== 'DRAFT') {
          toast({
            title:
              bill.status === 'PENDING_DISCOUNT_APPROVAL'
                ? 'This bill is waiting for discount approval — approve or reject it first.'
                : `Bill ${bill.bill_number} was already ${bill.status === 'VOID' ? 'voided' : 'completed'}.`,
            variant: 'error',
          });
          return;
        }
        // Draft locking: claim the bill so no other terminal edits it while we
        // hold it (a resumed park holds it already — this refreshes the claim).
        // 409 BILL_LOCKED names the holder; MANAGER+ may take over.
        try {
          await billingService.acquireBillLock(billId);
        } catch (lockErr) {
          if (lockErr instanceof ApiError && lockErr.statusCode === 409 && /being edited by/i.test(lockErr.message)) {
            if (canTakeOver && window.confirm(`${lockErr.message}.\nTake over this bill?`)) {
              await billingService.acquireBillLock(billId, true);
            } else {
              toast({ title: lockErr.message, variant: 'error' });
              return;
            }
          } else {
            throw lockErr;
          }
        }
        // GET /bills/:id carries no display unit; the local catalogue does.
        const units: Record<string, string> = {};
        try {
          const rows = await posDb.products.bulkGet(bill.items.map((i) => i.product_id));
          rows.forEach((row) => {
            if (row?.unit) units[row.id] = row.unit;
          });
        } catch {
          /* IndexedDB unavailable — lines fall back to PCS */
        }
        const autoParked = await hydrateFromServerBill(bill, units);
        if (autoParked) {
          toast({ title: `Your current bill was parked as #${autoParked}`, variant: 'info' });
        }
        toast({ title: 'Draft resumed — paying completes this same bill.', variant: 'success' });
      } catch (err) {
        toast({
          title: err instanceof ApiError ? err.message : 'Could not load that bill. Please try again.',
          variant: 'error',
        });
      }
    },
    [currentLocationId, hydrateFromServerBill, profile, refreshParkedBills, toast],
  );

  // ── Resume bill from query param ──────────────────────────────────────────
  //
  // A park still on this till (not uploaded yet) resumes from IndexedDB; any
  // other id — the store's parked list, or a DRAFT from Bill History / Bill
  // Detail — is resumed from the server. The param is only stripped once
  // handled, so an offline attempt can retry when the connection returns.
  const serverResumeInFlight = useRef<string | null>(null);
  const offlineResumeNoticeFor = useRef<string | null>(null);
  useEffect(() => {
    const resumeId = searchParams.get('resume');
    if (!resumeId || !isCartHydrated) return;
    if (serverResumeInFlight.current === resumeId) return;

    const stripParam = () => {
      // Copy before mutating — the URLSearchParams returned by useSearchParams
      // is a shared instance and mutating it in place is a react-router footgun
      // (see also the `?mode=quote` effect below; if both effects fire on the
      // same render the mutation ordering becomes non-obvious).
      const next = new URLSearchParams(searchParams);
      next.delete('resume');
      setSearchParams(next, { replace: true });
    };

    // Resume a park kept on this till
    if (parkedBills.some((b) => b.id === resumeId && b.localState)) {
      stripParam();
      resumeBill(resumeId);
      return;
    }

    if (!isConnected) {
      if (offlineResumeNoticeFor.current !== resumeId) {
        offlineResumeNoticeFor.current = resumeId;
        toast({ title: 'Reconnect to resume this bill — it is saved on the server.', variant: 'error' });
      }
      return;
    }

    serverResumeInFlight.current = resumeId;
    void resumeServerBill(resumeId).finally(() => {
      serverResumeInFlight.current = null;
      stripParam();
    });
  }, [
    searchParams,
    setSearchParams,
    parkedBills,
    resumeBill,
    resumeServerBill,
    isCartHydrated,
    isConnected,
    toast,
  ]);

  // ── The store's parked list (gst-parked 120) ──────────────────────────────
  // Uploads this till's offline parks and reloads the list on arrival, on a
  // store switch and when the connection returns; reloads it whenever any
  // till at the store parks, resumes or voids one, and when the till regains
  // focus.
  useEffect(() => {
    if (!isCartHydrated) return;
    void syncParkedBills();
  }, [isCartHydrated, currentLocationId, isConnected, syncParkedBills]);

  useEffect(() => {
    const onParkedChange = (p: { location_id?: string } | undefined) => {
      const here = useLocationStore.getState().currentLocationId;
      if (!p?.location_id || p.location_id === here) void refreshParkedBills();
    };
    const unsubscribers = (['bill:parked', 'bill:resumed', 'bill:voided'] as const).map((event) =>
      onSocketEvent<{ location_id?: string }>(event, onParkedChange),
    );
    const onFocus = () => {
      void refreshParkedBills();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      unsubscribers.forEach((off) => off());
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshParkedBills]);

  // Opening the Parked drawer reloads the list too, so a missed event never
  // leaves a bill another till already resumed on offer (QA GP2-002).
  useEffect(() => {
    if (showParkPanel) void refreshParkedBills();
  }, [showParkPanel, refreshParkedBills]);

  // ── Edit a quote from `?edit=<quoteId>` (Quote detail → Edit) ─────────────
  //
  // Loads the ACTIVE quote's lines and customer into the cart in quotation
  // mode; saving then PUTs the same quote (see saveQuote) instead of creating
  // a second one.
  const quoteEditInFlight = useRef<string | null>(null);
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !isCartHydrated) return;
    if (quoteEditInFlight.current === editId) return;
    quoteEditInFlight.current = editId;
    (async () => {
      try {
        const quote = await quoteService.getQuote(editId);
        if (quote.status !== 'ACTIVE') {
          toast({ title: `Quote ${quote.quote_number} is ${quote.status.toLowerCase()} and can no longer be edited.`, variant: 'error' });
          return;
        }
        const units: Record<string, string> = {};
        try {
          const rows = await posDb.products.bulkGet(quote.items.map((i) => i.product_id));
          rows.forEach((row) => {
            if (row?.unit) units[row.id] = row.unit;
          });
        } catch {
          /* IndexedDB unavailable — lines fall back to PCS */
        }
        const autoParked = await loadQuoteForEdit(
          quote as unknown as Parameters<typeof loadQuoteForEdit>[0],
          units,
        );
        if (autoParked) {
          toast({ title: `Your current bill was parked as #${autoParked}`, variant: 'info' });
        }
        toast({ title: `Editing quote ${quote.quote_number} — save to update it.`, variant: 'info' });
      } catch (err) {
        toast({
          title: err instanceof ApiError ? err.message : 'Could not load that quote. Please try again.',
          variant: 'error',
        });
      } finally {
        quoteEditInFlight.current = null;
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams, isCartHydrated, loadQuoteForEdit, toast]);

  // ── Bill an open quote from `?bill-quote=<id>` (Quote detail → Bill, T10) ──
  const quoteBillInFlight = useRef<string | null>(null);
  useEffect(() => {
    const quoteId = searchParams.get('bill-quote');
    if (!quoteId || !isCartHydrated) return;
    if (quoteBillInFlight.current === quoteId) return;
    quoteBillInFlight.current = quoteId;
    (async () => {
      try {
        const quote = await quoteService.getQuote(quoteId);
        if (quote.status !== 'ACTIVE' && quote.status !== 'APPROVED') {
          toast({ title: `Quote ${quote.quote_number} is ${quote.status.toLowerCase()} and can no longer be billed.`, variant: 'error' });
          return;
        }
        const units: Record<string, string> = {};
        try {
          const rows = await posDb.products.bulkGet(quote.items.map((i) => i.product_id));
          rows.forEach((row) => {
            if (row?.unit) units[row.id] = row.unit;
          });
        } catch {
          /* IndexedDB unavailable — lines fall back to PCS */
        }
        const autoParked = await loadQuoteForBilling(
          quote as unknown as Parameters<typeof loadQuoteForBilling>[0],
          units,
        );
        if (autoParked) {
          toast({ title: `Your current bill was parked as #${autoParked}`, variant: 'info' });
        }
        toast({ title: `Billing quote ${quote.quote_number} — edit the lines if needed, then pay.`, variant: 'info' });
      } catch (err) {
        toast({
          title: err instanceof ApiError ? err.message : 'Could not load that quote. Please try again.',
          variant: 'error',
        });
      } finally {
        quoteBillInFlight.current = null;
        const next = new URLSearchParams(searchParams);
        next.delete('bill-quote');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams, isCartHydrated, loadQuoteForBilling, toast]);

  // ── Force bill mode from `?mode=quote` (G2.15 entry point) ────────────────
  //
  // QuotationListPage / QuotationDetailPage navigate to /pos/billing?mode=quote
  // to start a new quote. Without this effect the cart stays in whatever mode
  // was last persisted (zustand persist), silently dropping the cashier into
  // bill mode after a fresh session or a completed sale. FR-BILL-033.
  //
  // If the cart already has items in SALE mode, surface a non-blocking toast
  // so the cashier sees the mode change instead of quietly relabelling a
  // half-built sale as a quote.
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode !== 'quote') return;

    const currentMode = useCartStore.getState().billMode;
    const currentItems = useCartStore.getState().items;
    if (quotesLocked) {
      showQuotesLockedToast();
    } else if (currentMode !== 'quotation') {
      setBillMode('quotation');
      if (currentItems.length > 0) {
        toast({
          title: 'Switched cart to Quotation mode',
          description: 'Existing items remain; no stock will be deducted.',
          variant: 'info',
        });
      }
    }

    const next = new URLSearchParams(searchParams);
    next.delete('mode');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setBillMode, toast, quotesLocked, showQuotesLockedToast]);

  // ── Search box reset after an add (C01) ─────────────────────────────────
  // The page owns the box text so it can empty the text and the grid filter
  // together (the #97 rule: they always match).
  const [searchText, setSearchTextState] = useState('');
  const searchTextRef = useRef('');
  const setSearchText = useCallback((text: string) => {
    searchTextRef.current = text;
    setSearchTextState(text);
  }, []);
  const productSearchRef = useRef(productSearchQuery);
  productSearchRef.current = productSearchQuery;

  // Put the cursor back in the box for the next item — only on large screens,
  // where the grid stays on screen (no on-screen keyboard on phones). Waits
  // past the dialogs' 150 ms focus hand-back and never takes focus from an
  // open dialog.
  const refocusTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(refocusTimerRef.current), []);
  const scheduleSearchRefocus = useCallback(() => {
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(min-width: 1024px)').matches) return;
    clearTimeout(refocusTimerRef.current);
    refocusTimerRef.current = setTimeout(() => {
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      scannerRef.current?.querySelector('input')?.focus();
    }, 250);
  }, []);

  // After an item lands in the cart: empty the box; if a search filter was on,
  // drop it and go back to "All". Browsing a tab with no search keeps the tab.
  // Held in a ref so tryAddItem's deps do not churn.
  const afterAddRef = useRef<() => void>(() => undefined);
  afterAddRef.current = () => {
    if (searchTextRef.current) setSearchText('');
    if (productSearchRef.current) {
      clearSearch();
      setActiveCategory(undefined);
    }
    scheduleSearchRefocus();
  };

  // ── Handlers ─────────────────────────────────────────────────────────────

  // #308: single guarded entry point for every add-to-cart path (tile,
  // barcode, hardware scan, image match, qty prompt, combos). The cart store
  // refuses adds that exceed recipe availability; surface the limiting
  // ingredient so the cashier learns why immediately.
  const tryAddItem = useCallback(
    (product: Product, quantity = 1): boolean => {
      if (addItem(product, quantity)) {
        afterAddRef.current();
        return true;
      }
      const shortage = useCartStore.getState().ingredientAvailability[product.id];
      toast({
        title: shortage
          ? `Cannot add ${product.name} — insufficient ingredient '${shortage.limitingIngredient}' (only ${shortage.available} portion${shortage.available === 1 ? '' : 's'} available)`
          : `Cannot add ${product.name} — insufficient ingredient stock`,
        variant: 'warning',
      });
      return false;
    },
    [addItem, toast],
  );

  // Expand a combo into its component dishes, then apply a flat per-line
  // discount so the cart total matches the combo price exactly. Each line is a
  // real product, so kitchen tickets, stock/recipe deduction and GST stay correct.
  const handleComboSelect = useCallback(
    async (combo: Combo) => {
      try {
        const fulls = await Promise.all(
          combo.items.map((it) => productsService.getById(it.product_id)),
        );
        const discounts = comboLineDiscounts(combo.items, combo.combo_price);

        // #308: components blocked by ingredient shortage toast individually
        // via tryAddItem; the success toast below only fires when the whole
        // combo made it into the cart.
        const addResults = combo.items.map((it, idx) => {
          const full = fulls[idx];
          return full ? tryAddItem(full, it.quantity) : false;
        });
        const allAdded = addResults.every(Boolean);

        // Apply the combo discount to each component line (by productId).
        for (const line of useCartStore.getState().items) {
          const discount = discounts.get(line.productId);
          if (discount) applyItemDiscount(line.id, 'FLAT', discount);
        }

        setMobileView('cart');
        if (allAdded) {
          toast({ title: `Added combo "${combo.name}".`, variant: 'success' });
        }
      } catch {
        toast({ title: `Failed to add combo "${combo.name}".`, variant: 'error' });
      }
    },
    [tryAddItem, applyItemDiscount, toast],
  );

  // QA IS1-005 (owner decision ID8): a retail till may sell below zero stock,
  // but the cashier confirms first. Tile, scan and typed-code adds all ask
  // here. Restaurant menu items carry no stock (recipes do), and a quote or
  // return moves none, so those add straight away.
  const [outOfStockAdd, setOutOfStockAdd] = useState<{ product: Product; inCart: number; proceed: () => void } | null>(null);
  const confirmIfOutOfStock = useCallback(
    (product: Product, proceed: () => void) => {
      const inCart = useCartStore.getState().items.find((i) => i.productId === product.id)?.quantity ?? 0;
      // Units held for a wastage approval are on hand but not sellable (QA IS4-002).
      const sellable = Number(product.stock_quantity) - Number(product.held_quantity ?? 0);
      if (isRestaurantTenant || billMode !== 'sale' || inCart + 1 <= sellable) {
        proceed();
        return;
      }
      setOutOfStockAdd({ product, inCart, proceed });
    },
    [isRestaurantTenant, billMode],
  );

  // Adds a resolved product (a grid parent or a picked variant child) to the
  // cart, going through the modifier panel / quantity prompt when configured.
  const addSelectedProductNow = useCallback(
    (product: Product) => {
      // If this product has add-on groups, open the panel first; the surcharge is
      // folded into the line's unit price on confirm. Otherwise add straight away.
      const groups = groupsForProduct(product.id);
      if (groups.length > 0) {
        if (!tryAddItem(product)) return;
        const line = useCartStore.getState().items.find((i) => i.productId === product.id);
        if (line) {
          const defaults: Record<string, number> = {};
          for (const g of groups) for (const o of g.options) defaults[o.id] = o.additionalPrice;
          setSelectedModifiers({});
          setEditedPrices(defaults);
          setPendingModifier({
            lineId: line.id,
            productName: product.name,
            basePrice: product.price,
            groups,
          });
        }
        setMobileView('cart');
        return;
      }

      // Quick-add OFF (Terminal Preferences) → ask for the quantity instead
      // of silently adding 1 unit.
      if (!quickAddQuantity) {
        setQtyInput('1');
        setPendingQtyProduct(product);
        return;
      }

      if (!tryAddItem(product)) return;
      // On mobile, switch to cart view when item added
      setMobileView('cart');
    },
    [tryAddItem, groupsForProduct, quickAddQuantity],
  );
  const addSelectedProduct = useCallback(
    (product: Product) => confirmIfOutOfStock(product, () => addSelectedProductNow(product)),
    [confirmIfOutOfStock, addSelectedProductNow],
  );

  const handleProductSelect = useCallback(
    (gridItem: ProductGridItem) => {
      if (gridItem.id.startsWith('combo:')) {
        const combo = combos.find((c) => `combo:${c.id}` === gridItem.id);
        if (!combo || !combo.is_active || !isComboAvailableNow(combo)) {
          toast({ title: 'This combo is no longer available.', variant: 'warning' });
          return;
        }
        void handleComboSelect(combo);
        return;
      }
      // Grid tiles are parents; a picked variant is one of the children loaded
      // for the picker. Never silently drop the add — fetch the row if needed.
      const product = resolveSelectedProduct(gridItem.id, products, loadedVariantsRef.current);
      if (product) {
        addSelectedProduct(product);
        return;
      }
      productsService
        .getById(gridItem.id)
        .then((full) => {
          loadedVariantsRef.current.set(full.id, full);
          addSelectedProduct(full);
        })
        .catch(() => toast({ title: `Could not load ${gridItem.name}.`, variant: 'warning' }));
    },
    [products, combos, handleComboSelect, toast, addSelectedProduct],
  );

  // A measured product (kg, g, l, ml, m) takes up to 3 decimals (IS1-006).
  const qtyToAdd = parseQuantityForUnit(qtyInput, pendingQtyProduct?.unit);
  const handleQuantityConfirm = useCallback(() => {
    if (!pendingQtyProduct || !(qtyToAdd > 0)) return;
    // Blocked adds keep the prompt open so the cashier can lower the quantity.
    if (!tryAddItem(pendingQtyProduct, qtyToAdd)) return;
    setPendingQtyProduct(null);
    setMobileView('cart');
  }, [pendingQtyProduct, qtyToAdd, tryAddItem]);

  // Confirm the add-on panel: sum the selected option prices and fold the
  // surcharge into the line's unit price (server re-snapshots price from it).
  const handleConfirmModifiers = useCallback(() => {
    if (!pendingModifier) return;
    const { lineId, basePrice, groups } = pendingModifier;
    let surcharge = 0;
    const names: string[] = [];
    for (const group of groups) {
      const chosen = selectedModifiers[group.id] ?? [];
      for (const optId of chosen) {
        const opt = group.options.find((o) => o.id === optId);
        if (!opt) continue;
        surcharge += editedPrices[optId] ?? opt.additionalPrice;
        names.push(opt.name);
      }
    }
    setLineModifiers(lineId, basePrice + surcharge, names.length > 0 ? names.join(', ') : undefined);
    setPendingModifier(null);
    setSelectedModifiers({});
    setEditedPrices({});
    scheduleSearchRefocus();
  }, [pendingModifier, selectedModifiers, editedPrices, setLineModifiers, scheduleSearchRefocus]);

  const handleCancelModifiers = useCallback(() => {
    // Leave the line as-added (base price, no add-ons).
    setPendingModifier(null);
    setSelectedModifiers({});
    setEditedPrices({});
    scheduleSearchRefocus();
  }, [scheduleSearchRefocus]);

  const addOnTotal = (pendingModifier?.groups ?? []).reduce((sum, group) => {
    const chosen = selectedModifiers[group.id] ?? [];
    return sum + group.options
      .filter((o) => chosen.includes(o.id))
      .reduce((s, o) => s + (editedPrices[o.id] ?? o.additionalPrice), 0);
  }, 0);

  const handleBarcodeScan = useCallback(
    async (barcode: string) => {
      const product = await findByBarcode(barcode);
      if (product) {
        confirmIfOutOfStock(product, () => {
          if (tryAddItem(product)) setMobileView('cart');
        });
      } else {
        toast({ title: `Product not found for barcode ${barcode}.`, variant: 'warning' });
      }
    },
    [findByBarcode, tryAddItem, toast, confirmIfOutOfStock],
  );

  // Keyboard-wedge captures go through the configured prefix/suffix/min-length
  // rules from Settings → Scanner before they reach the product lookup.
  const handleHardwareScan = useCallback(
    (raw: string) => {
      // A scan is never a search: when the box had focus the scanned digits
      // landed in it too. Take them out so the next scan does not join on and
      // the text still matches the grid filter (#97).
      const text = searchTextRef.current;
      if (raw && text.endsWith(raw)) {
        const left = text.slice(0, text.length - raw.length);
        setSearchText(left);
        // Each scanned digit also queued a type-search; set the filter back to
        // what is left in the box (C02).
        if (left) searchProducts(left, true);
        else if (productSearchRef.current) clearSearch();
      }
      const code = normalizeScannedBarcode(raw, scannerPrefs);
      if (code) handleBarcodeScan(code);
    },
    [handleBarcodeScan, scannerPrefs, setSearchText, searchProducts, clearSearch],
  );

  // Image lookup returns a "lite" product shape; refetch the full product
  // so the cart has gst_rate, cost, hsn_code needed for billing totals.
  const handleImageMatchPick = useCallback(
    async (lite: { id: string; name: string }) => {
      try {
        const full = await productsService.getById(lite.id);
        if (!full) {
          toast({ title: `Could not load ${lite.name}.`, variant: 'warning' });
          return;
        }
        if (!tryAddItem(full)) return;
        setMobileView('cart');
        toast({ title: `Added ${full.name} to cart.`, variant: 'success' });
      } catch {
        toast({ title: `Failed to add ${lite.name}.`, variant: 'error' });
      }
    },
    [tryAddItem, toast],
  );

  // An exact SKU or barcode typed by hand adds the item, as a scan would;
  // anything else filters the grid.
  const handleManualSearch = useCallback(
    async (query: string) => {
      const exact = query ? await findByBarcode(query) : null;
      if (exact) {
        confirmIfOutOfStock(exact, () => {
          if (tryAddItem(exact)) setMobileView('cart');
        });
        return;
      }
      if (query) {
        // A new search looks in every category, so "All" is lit. Enter does
        // not wait for the type-search pause.
        setActiveCategory(undefined);
        searchProducts(query, true);
      } else if (productSearchRef.current) {
        // The box was emptied: drop the search at once and show everything.
        clearSearch();
        setActiveCategory(undefined);
      }
    },
    [findByBarcode, tryAddItem, searchProducts, clearSearch, confirmIfOutOfStock],
  );

  // The grid filters as the cashier types, after the hook's 0.3 s pause (C02).
  // Never an exact-code add here: that waits for Enter, or "1234" would add
  // the item coded "123" mid-word. An emptied box is handled by the box's own
  // clear path (handleManualSearch('')).
  const handleSearchTextChange = useCallback(
    (text: string) => {
      setSearchText(text);
      if (!text) return;
      setActiveCategory(undefined);
      searchProducts(text);
    },
    [setSearchText, searchProducts],
  );

  const handleQuantityChange = useCallback(
    (id: string, qty: number) => {
      updateQuantity(id, qty);
    },
    [updateQuantity],
  );

  const handleDiscountChange = useCallback(
    (id: string, discount: number) => {
      if (discount > 0) {
        applyItemDiscount(id, 'FLAT', discount);
      } else {
        clearItemDiscount(id);
      }
    },
    [applyItemDiscount, clearItemDiscount],
  );

  const handleRemoveItem = useCallback(
    (id: string) => {
      removeItem(id);
    },
    [removeItem],
  );

  const handleCustomerSelect = useCallback(
    (customer: CustomerLookupResult) => {
      setCustomer(customer);
      // Attaching a customer overrides any earlier walk-in skip.
      setCustomerSkipped(false);
    },
    [setCustomer],
  );

  // Reset the walk-in skip flag whenever the cart empties — each new bill
  // should re-prompt for customer capture on the next Pay click.
  useEffect(() => {
    if (items.length === 0 && customerSkipped) {
      setCustomerSkipped(false);
    }
  }, [items.length, customerSkipped]);

  const handleCustomerClear = useCallback(() => {
    setCustomer(null);
  }, [setCustomer]);

  const handleCustomerSearch = useCallback(
    async (query: string): Promise<CustomerLookupResult[]> => {
      const res = await customersService.list({ search: query, limit: 10, active: true });
      return res.data.map((c) => ({
        id: c.id,
        name: c.name,
        phone: c.phone,
        loyaltyPoints: c.loyalty_points,
        store_credit_balance: c.store_credit_balance,
      }));
    },
    [],
  );

  const handleBillModeChange = useCallback(
    (mode: BillMode) => {
      // Return mode in the cart is a *shortcut* into the bill-history
      // workflow: pick the original bill there, then drive the existing
      // Process-Return flow from BillDetailPage. This keeps the credit-note
      // engine on one code path (POST /bills/:id/return) regardless of
      // where the cashier started. The Return mode itself is filtered out
      // of the pill row when the cashier lacks permission, so this branch
      // only fires for authorised users.
      if (mode === 'return') {
        navigate('/pos/bills?action=return&status=COMPLETED');
        return;
      }
      if (mode === 'quotation' && quotesLocked) {
        showQuotesLockedToast();
        return;
      }
      setBillMode(mode);
    },
    [navigate, setBillMode, quotesLocked, showQuotesLockedToast],
  );

  /**
   * Gate between "ready to pay" and the Payment modal. SERIAL-tracked lines
   * must name their units first — the sale cannot be attributed to specific
   * stock otherwise, and the server would reject the completion anyway.
   */
  const [paymentAwaitingModes, setPaymentAwaitingModes] = useState(false);
  const proceedToPayment = useCallback(() => {
    // The tracking lookup for this cart is still in flight: deciding now would
    // open Payment without asking for serials. Resume once it lands.
    if (trackingModesPending || expiredBatchStockPending) {
      setPaymentAwaitingModes(true);
      return;
    }
    if (serialsOutstanding) {
      setShowSerialCapture(true);
      return;
    }
    // QA IS4-004: units from an expired batch need the cashier's OK first.
    if (expiredBatchAccepted.length > 0 && expiredBatchAcceptedKey !== expiredBatchAcceptKey) {
      setShowExpiredBatchConfirm(true);
      return;
    }
    setShowPayment(true);
  }, [
    serialsOutstanding, trackingModesPending, expiredBatchStockPending,
    expiredBatchAccepted, expiredBatchAcceptedKey, expiredBatchAcceptKey,
  ]);
  proceedToPaymentRef.current = proceedToPayment;
  useEffect(() => {
    if (!paymentAwaitingModes || trackingModesPending || expiredBatchStockPending) return;
    setPaymentAwaitingModes(false);
    if (items.length > 0) proceedToPaymentRef.current();
  }, [paymentAwaitingModes, trackingModesPending, expiredBatchStockPending, items.length]);

  /**
   * Persist the cart as a quote for `customer`. Takes the customer explicitly
   * (rather than reading the store) so the capture-modal path can save in the
   * same tick it attaches — the store write isn't visible to closures until
   * the next render.
   */
  const saveQuote = useCallback(async (customer: CustomerLookupResult) => {
    if (!currentLocationId) return;
    setIsProcessing(true);
    setError(null);
    try {
      if (editingQuote) {
        // ── Editing an existing quote: replace its lines on the server ──
        if (!isConnected) {
          setError('Reconnect to save changes to this quote.');
          return;
        }
        const updated = await quoteService.updateQuote(editingQuote.id, {
          location_id: editingQuote.locationId,
          customer_id: customer.id,
          items: items.map((item) => ({
            product_id: item.productId,
            quantity: item.quantity,
            ...(item.discountAmount > 0 ? { discount: item.discountAmount } : {}),
          })),
        });
        clearCart();
        setCreatedQuoteId(editingQuote.id);
        setSuccessMsg(`Quote ${updated.quote_number ?? editingQuote.quoteNumber} updated`);
        hideSuccessAfter(15000);
      } else if (isConnected) {
        // ── Online quote: create via API ──
        const quoteItems = items.map((item) => ({
          product_id: item.productId,
          quantity: item.quantity,
          ...(item.discountValue > 0 ? { discount: item.discountAmount } : {}),
        }));
        const created = await quoteService.createQuote({
          location_id: currentLocationId,
          items: quoteItems,
          customer_id: customer.id,
          validity_days: 3,
        });
        clearCart();
        setCreatedQuoteId(created.id);
        setSuccessMsg(`Quote ${created.quote_number} created`);
        // Long enough to actually click "Open quote" — 5s was a glance.
        hideSuccessAfter(15000);
      } else {
        // ── Offline quote: persist to IndexedDB and enqueue for sync ──
        // FR-BILL-033, FR-SYNC-006. PAGES.md §5.4.
        const tenantId = useTenantStore.getState().tenant?.id;
        if (!tenantId) {
          setError('No tenant context — cannot save quote offline');
          return;
        }
        // Prefer the real short code from /auth/me (plumbed through
        // use-location-store) so offline quote numbers match the server
        // format. Fall back to a name-derived hack only if the code is
        // missing (e.g., stale session from before the upgrade).
        const locationState = useLocationStore.getState();
        const locationCode =
          locationState.currentLocationCode
          || locationState.currentLocationName?.toUpperCase().replace(/\s+/g, '-').slice(0, 6)
          || 'OFL';
        await offlineQuoteService.createQuoteOffline({
          items,
          tenantId,
          locationId: currentLocationId,
          locationCode,
          customerId: customer.id,
          customerName: customer.name,
          customerPhone: customer.phone,
          isInterState,
          validityDays: 3,
        });
        clearCart();
        setSuccessMsg('Quote saved offline — will sync automatically when connected');
        hideSuccessAfter(5000);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create quote.');
    } finally {
      setIsProcessing(false);
    }
  }, [items, currentLocationId, isConnected, isInterState, clearCart, editingQuote]);

  const handleCheckout = useCallback(async () => {
    if (items.length === 0) return;
    if (!currentLocationId) {
      setError('Select a location before billing');
      return;
    }
    // Quote mode: a quote is always addressed to a customer — no walk-in
    // skip. Capture one first, then save directly (no payment modal).
    if (billMode === 'quotation') {
      if (!selectedCustomer) {
        setShowCustomerCapture(true);
        return;
      }
      await saveQuote(selectedCustomer);
      return;
    }
    if (!(await requireShift())) return;
    // Sale / Return: prompt the cashier to capture a customer (or
    // explicitly skip) before opening the Payment modal. Already-attached
    // customer or an earlier skip on the same draft bypasses the prompt.
    if (!selectedCustomer && !customerSkipped) {
      setShowCustomerCapture(true);
      return;
    }
    proceedToPayment();
  }, [items, currentLocationId, billMode, selectedCustomer, customerSkipped, saveQuote, proceedToPayment, requireShift]);

  /**
   * Everything after the API settles a bill online: receipt, success banner,
   * re-order QR, auto-print. Shared by checkout and by the retry after a
   * manager approves a sale over the credit limit (T08).
   */
  /**
   * Auto-print the receipt if enabled and a thermal printer is connected.
   * Shared by online sales and (T19) offline ones, which print their OFL number.
   */
  const autoPrintReceipt = useCallback(
    (bill: BillWithItems, method: PaymentMethod) => {
      const printerState = usePrinterStore.getState();
      if (printerState.autoPrint && printerState.connectionStatus === 'connected' && printerState.connectionType !== 'browser') {
        // Built from `completed`, so the printed receipt carries the real
        // status, payment method, amount paid and change due. The tax
        // options make the labels follow the tenant's regime (GST vs VAT)
        // instead of silently defaulting to India.
        const receiptData = buildReceiptData(
          bill,
          useTenantStore.getState().tenant,
          useLocationStore.getState().currentLocation,
        );
        if (receiptData) {
          printerState.printReceipt(
            receiptData.billData,
            receiptData.tenantData,
            receiptData.locationData,
            {
              taxName: tax.taxName,
              taxIdLabel: tax.taxIdLabel,
              splitLabels: tax.splitLabels,
              showHsn: tax.showHsn,
            },
          );
          // Kick cash drawer on cash payment
          if (printerState.kickCashDrawer && method === 'cash') {
            printerState.openCashDrawer();
          }
        }
      } else if (printerState.autoPrint && printerState.connectionStatus !== 'connected') {
        setSuccessMsg('Bill completed — printer not connected, receipt not printed');
      }
    },
    [tax],
  );

  const finishOnlineSale = useCallback(
    (completed: BillWithItems, method: PaymentMethod) => {
      setShowPayment(false);
      // `completed` is a plain local object returned by the API, so it
      // survives clearCart() wiping the store.
      setReceiptOffline(false);
      setReceiptBill(completed);
      // gst-parked D7: the server keeps the IGST switch only with 'Edit taxes'
      // or a manager approval that is still valid; otherwise it bills the
      // store's own split, which the receipt shows.
      const { supplyOverride } = useCartStore.getState();
      const switchDropped =
        !!supplyOverride && !!completed.supply_type && completed.supply_type !== supplyOverride;
      clearCart();
      setSuccessMsg(
        `Bill ${completed.bill_number} completed — ${formatCurrency(Number(completed.grand_total) + Number(completed.rounding_adjustment ?? 0), tenantCurrency)} paid` +
          (completed.credit_note_remainder
            ? ` — new credit note ${completed.credit_note_remainder.redemption_code} issued for ${formatCurrency(Number(completed.credit_note_remainder.amount), tenantCurrency)}`
            : '') +
          (switchDropped ? ' — the tax switch was not approved, so the bill uses the store’s tax type' : ''),
      );

      // G4.3: offer a re-order QR if we have contact details on file.
      // Modal is dismissable — cashier can skip if customer declines.
      // Held until the receipt modal is dismissed: both are `fixed inset-0
      // z-50` and would otherwise stack on top of each other.
      // Skipped when `marketing` is not on the plan: the API would refuse it.
      // Skipped too when the customer opted out of marketing WhatsApp (CU4-003).
      const phone = selectedCustomer?.phone ?? null;
      if (phone && hasPlanFeature('marketing') && !selectedCustomer?.whatsapp_opt_out) {
        setPendingReorderBill({
          billId: completed.id,
          billNumber: completed.bill_number,
          phone,
          email: null,
        });
      }

      // ── Auto-print receipt if enabled & printer connected ──
      autoPrintReceipt(completed, method);
    },
    [selectedCustomer, clearCart, tenantCurrency, autoPrintReceipt, hasPlanFeature],
  );

  /**
   * T19: a bill saved offline gets the same receipt and auto-print as an
   * online one, carrying its temporary OFL number (Shopify-style); the final
   * number arrives when it syncs.
   */
  const finishOfflineSale = useCallback(
    (bill: BillWithItems, method: PaymentMethod) => {
      setShowPayment(false);
      setReceiptOffline(true);
      setReceiptBill(bill);
      clearCart();
      setSuccessMsg(`Bill ${bill.bill_number} saved offline — it gets its final number when it syncs`);
      autoPrintReceipt(bill, method);
    },
    [clearCart, autoPrintReceipt],
  );

  const handlePaymentConfirm = useCallback(
    async (
      method: PaymentMethod,
      splits?: SplitPayment[],
      _denominations?: DenominationRow[],
      creditNote?: CreditNoteLookupResult,
      cashTendered?: number,
    ) => {
      if (!currentLocationId) {
        setError('No location selected — cannot process payment');
        return;
      }
      // A sale waiting for a manager's PIN (T08) owns its draft; a second
      // confirm (e.g. F10 behind the PIN dialog) must not start another.
      if (pendingCreditApproval) return;
      // T19: a slip confirmation is good for this one checkout only, even if the
      // connection came back meanwhile and the sale goes online instead.
      const confirmedSlip = offlineSlipConfirmedRef.current;
      offlineSlipConfirmedRef.current = null;
      setIsProcessing(true);
      setError(null);
      setPriceNotice(null);
      // Drafts carry a placeholder number; the INV- number is assigned by the
      // server at completion. If completion fails we still void a draft WE
      // created here so a retry does not leave stale DRAFT rows behind in Bill
      // History. A resumed draft (currentBillId) is never voided on failure —
      // the cashier can retry or park it.
      let draftId: string | null = null;
      let completedOk = false;
      let lastCompletion: { billId: string; payload: CompletePayload } | null = null;
      try {
        if (currentBillId && !isConnected) {
          throw new Error(
            'This bill is saved on the server — reconnect to complete it, or park it for later.',
          );
        }
        if (isConnected) {
          // ── Online flow: create (or sync the resumed draft) & complete via API ──
          const billItems = items.map(toBillItem);

          let billId: string;
          let discountAlreadyOnServer = false;
          if (currentBillId) {
            // Resumed server draft: complete THAT bill (same id, same number
            // sequence) instead of creating a second one. Re-read it so a
            // stale cart never overwrites a bill settled on another terminal.
            billId = currentBillId;
            const server = await billingService.getBill(currentBillId);
            if (server.status !== 'DRAFT') {
              throw new Error(RESUMED_BILL_NOT_DRAFT_MSG);
            }
            // PATCH /bills/:id is a line diff: existing lines are edited in
            // place (keeping their price snapshot) and only genuinely new
            // products are priced by the server. The server keeps its stored
            // bill discount across the edit, so nothing is sent when the cart
            // matches the server.
            const lineDiff = diffDraftLines(server.items, items);
            if (lineDiff) {
              await billingService.updateDraftBill(currentBillId, lineDiff);
            }
            // Bill discount: the server already holds the one we resumed with;
            // only a cashier change needs a call (clear, or re-apply below).
            const serverHadBillDiscount =
              !!server.discount_details ||
              Number(server.discount_total ?? 0) -
                server.items.reduce((sum, i) => sum + Number(i.line_discount ?? 0), 0) >
                0;
            if (!billDiscount && serverHadBillDiscount) {
              await billingService.clearDiscount(currentBillId);
            } else if (
              billDiscount?.appliedBy === RESUMED_DISCOUNT_APPLIED_BY ||
              billDiscount?.appliedBy === APPROVED_DISCOUNT_APPLIED_BY
            ) {
              // An approved discount was written to the bill by the approval
              // consumer, exactly like a resumed one. Re-posting it sent the
              // full value back through the role cap and 403'd mid-payment.
              discountAlreadyOnServer = true;
            }
          } else {
            const draft = await billingService.createBill({
              location_id: currentLocationId,
              items: billItems,
              customer_id: selectedCustomer?.id,
            });
            billId = draft.id;
            draftId = draft.id;
          }

          if (billDiscount && !discountAlreadyOnServer) {
            if (billDiscount.voucherCode) {
              // Voucher path — server applies the discount AND increments
              // used_count + writes a redemption row in one transaction.
              await vouchersService.redeem(billDiscount.voucherCode, {
                bill_id: billId,
                ...(selectedCustomer?.id ? { customer_id: selectedCustomer.id } : {}),
              });
            } else {
              await billingService.applyDiscount(billId, {
                type: billDiscount.type,
                value:
                  billDiscount.type === 'PERCENTAGE'
                    ? Math.round(billDiscount.value * 100)
                    : billDiscount.value,
                reason: billDiscount.reason,
              });
            }
          }

          // Every completion goes through here, so an over-limit refusal (T08)
          // knows which bill and payload to retry once a manager approves.
          // A billed quote (T10) is closed and linked by the same completion.
          const complete = (base: CompletePayload) => {
            // gst-parked D7: the cashier's IGST switch rides with the bill; with
            // none, nothing is sent and the server stamps the store's own split.
            const withSupply = { ...base, ...selectSupplyOverridePayload(useCartStore.getState()) };
            const withQuote = billingQuote ? { ...withSupply, quotation_id: billingQuote.id } : withSupply;
            // QA IS4-004: the expired-batch units the cashier accepted, for the audit log.
            const payload = expiredBatchAccepted.length > 0
              ? { ...withQuote, expired_batch_accepted: expiredBatchAccepted }
              : withQuote;
            lastCompletion = { billId, payload };
            return billingService.completeBill(billId, payload);
          };
          // Account Credit (T08): the customer's linked account, and over the
          // limit ask for a manager's PIN here instead of refusing outright.
          const accountCreditFields = (used: boolean) => {
            if (!used) return {};
            if (!creditAccount) {
              throw new Error('This customer has no credit account. Choose another payment method.');
            }
            return { credit_account_id: creditAccount.id, on_device_approval: true };
          };

          // Only cash is rounded (ops-simulation T01): every other tender is
          // charged the exact pre-rounding total, and the server rejects a
          // round-off on anything but CASH or a SPLIT with a cash leg.
          // Backend's `rounding_adjustment` carries the delta in either
          // direction so the bill's stored grand_total stays the exact figure.
          const effectiveRounding = cashRoundingForTender(method, splits, totals.roundOff);
          // Cash sends what was handed over, so the bill keeps the tender and the
          // server derives change_due (the payment row stays the net amount).
          const effectiveAmount = method === 'cash'
            ? Math.max(payableTotal, cashTendered ?? 0)
            : payableTotal - totals.roundOff + effectiveRounding;

          // Serial-tracked units, keyed by the SERVER's bill_item_id — the cart
          // line id is local and means nothing to the API. Matching by product
          // is safe because the cart holds one line per product.
          let serialsPayload: { bill_item_id: string; serial_numbers: string[] }[] | undefined;
          const linesWithSerials = items.filter((i) => (i.serials?.length ?? 0) > 0);
          if (linesWithSerials.length > 0) {
            const serverBill = await billingService.getBill(billId);
            const itemIdByProduct = new Map(
              (serverBill.items ?? []).map((si) => [String(si.product_id), String(si.id)]),
            );
            serialsPayload = linesWithSerials
              .map((line) => {
                const billItemId = itemIdByProduct.get(line.productId);
                return billItemId
                  ? { bill_item_id: billItemId, serial_numbers: line.serials ?? [] }
                  : null;
              })
              .filter((x): x is { bill_item_id: string; serial_numbers: string[] } => x !== null);
          }

          // The completion response — NOT the createBill/getBill draft — is the
          // settled bill. The draft's status is 'DRAFT', its
          // payment_method/amount_paid/change_due are empty, and its totals are
          // stale whenever a voucher or bill discount was applied after it was
          // fetched. Everything downstream (receipt, auto-print) must read from
          // this instead.
          let completed: BillWithItems;
          if (method === 'split' && splits?.length) {
            // The online DTO field is `method` (SplitPaymentDto). The offline
            // queue below uses `payment_method` — the sync service remaps it.
            // A credit-note leg (CU2-007) is a STORE_CREDIT line carrying the note's code.
            const splitLines: { method: BillPaymentMethod; amount: number; credit_note_code?: string }[] =
              splits.map((s) => ({
                method: PAYMENT_METHOD_MAP[s.method] as BillPaymentMethod,
                amount: s.amount,
                ...(s.creditNote ? { credit_note_code: s.creditNote.code } : {}),
              }));
            // WALLET / STORE_CREDIT tenders inside the split are debited from
            // the attached customer, so the API needs customer_id for them —
            // not a credit-note leg, which a walk-in can pay with.
            const splitNeedsCustomerId = splitLines.some(
              (l) => l.method === 'WALLET' || (l.method === 'STORE_CREDIT' && !l.credit_note_code) || l.method === 'CREDIT',
            );
            completed = await complete({
              payment_method: 'SPLIT',
              amount_paid: effectiveAmount,
              rounding_adjustment: effectiveRounding,
              ...(serialsPayload ? { serials: serialsPayload } : {}),
              payments: splitLines,
              ...(splitNeedsCustomerId && selectedCustomer?.id
                ? { customer_id: selectedCustomer.id }
                : {}),
              ...accountCreditFields(splitLines.some((l) => l.method === 'CREDIT')),
            });
          } else if (method === 'credit_note' && creditNote) {
            // Bearer credit-note redemption — pass the code so the
            // backend can mark the specific store_credits row used.
            // No customer_id needed; this path works for walk-ins too.
            completed = await complete({
              payment_method: 'STORE_CREDIT',
              amount_paid: effectiveAmount,
              rounding_adjustment: effectiveRounding,
              ...(serialsPayload ? { serials: serialsPayload } : {}),
              credit_note_code: creditNote.code,
            });
          } else {
            // WALLET / STORE_CREDIT (customer mode) / CREDIT all require
            // customer_id on the completion payload — the draft's denormalized
            // customer reference isn't enough.
            const needsCustomerId =
              method === 'wallet' || method === 'store_credit' || method === 'account_credit';
            // Card / UPI: push the amount to the terminal and finalise only once
            // it confirms. A decline, timeout or cancel throws, so the draft is
            // voided below and the cashier can retry with any method.
            if (method === 'upi' || method === 'card') {
              const abort = new AbortController();
              terminalAbortRef.current = abort;
              await settleOnTerminal({
                billId,
                amount: effectiveAmount,
                locationId: currentLocationId,
                signal: abort.signal,
                onUpdate: setTerminalWait,
              });
            }
            completed = await complete({
              payment_method: PAYMENT_METHOD_MAP[method],
              amount_paid: effectiveAmount,
              rounding_adjustment: effectiveRounding,
              ...(serialsPayload ? { serials: serialsPayload } : {}),
              ...(needsCustomerId && selectedCustomer?.id
                ? { customer_id: selectedCustomer.id }
                : {}),
              ...accountCreditFields(method === 'account_credit'),
            });
          }

          completedOk = true;
          finishOnlineSale(completed, method);
        } else {
          // ── Offline flow: save to IndexedDB queue ──
          // T19 (Odoo-style): cash, or card/UPI on a confirmed manual slip.
          // The store's card terminal, store credit, wallet points and Account
          // Credit all need the server, so they are refused with a reason.
          const refusal = offlineTenderRefusal(method, splits, {
            terminalLinked: isLocationTerminalLinked(currentLocationId),
          });
          if (refusal) throw new Error(refusal);
          if (billingQuote) throw new Error(QUOTE_BILL_OFFLINE_MSG);
          // Same rule as online: only cash (or a split with a cash leg) is rounded.
          const offlineRounding = cashRoundingForTender(method, splits, totals.roundOff);
          const offlineAmount = totals.grandTotal - totals.roundOff + offlineRounding;
          const slip = offlineSlipTender(method, splits, offlineAmount);
          if (slip && !confirmedSlip) {
            // Nothing is charged by the till: the cashier confirms the slip first.
            setOfflineSlipReference('');
            setOfflineSlip({ method, splits, cashTendered, methods: slip.methods, amount: slip.amount });
            return;
          }
          const locationName = useLocationStore.getState().currentLocationName;
          const offline = await offlineBillingService.createBillOffline({
            items,
            customerId: selectedCustomer?.id ?? null,
            locationId: currentLocationId,
            locationCode: locationName?.toUpperCase().replace(/\s+/g, '-').slice(0, 6) ?? 'OFL',
            paymentMethod: PAYMENT_METHOD_MAP[method],
            amountPaid: offlineAmount,
            roundingAdjustment: offlineRounding,
            isInterState: useCartStore.getState().isInterState,
            supplyOverride: useCartStore.getState().supplyOverride,
            billDiscount,
            payments: method === 'split' && splits?.length
              ? splits.map((s) => ({ payment_method: PAYMENT_METHOD_MAP[s.method], amount: s.amount }))
              : undefined,
            paymentReference: confirmedSlip?.reference || null,
            customerName: selectedCustomer?.name ?? null,
            customerPhone: selectedCustomer?.phone ?? null,
            receiptTotals: {
              subtotal: totals.subtotal,
              discountTotal: totals.totalDiscount + totals.totalBillDiscount,
              taxTotal: totals.taxTotal,
              grandTotal: totals.grandTotal - totals.roundOff,
              roundingAdjustment: offlineRounding,
            },
          });

          finishOfflineSale(offline.receiptBill, method);
        }
        hideSuccessAfter(4000);
      } catch (err) {
        // T08: over the credit limit. Keep the draft and ask for a manager's
        // PIN; the same bill completes with the approval token. (Assigned
        // inside `complete`, which TS's narrowing cannot see.)
        const last = lastCompletion as { billId: string; payload: CompletePayload } | null;
        if (err instanceof ApiError && err.code === 'APPROVAL_REQUIRED' && last?.payload.on_device_approval) {
          setPendingCreditApproval({
            billId: last.billId,
            draftId,
            payload: last.payload,
            method,
            reason: err.message,
            amount: accountCreditAmount(last.payload),
          });
          return;
        }
        if (draftId && !completedOk) {
          const orphanId = draftId;
          // Best-effort: the cashier's error banner must not depend on this.
          void (async () => {
            try {
              await billingService.voidBill(orphanId);
            } catch {
              /* leave the draft; it stays visible in Bill History */
            }
          })();
        }
        if (err instanceof ApiError && currentBillId && err.statusCode === 409 && /not in DRAFT/i.test(err.message)) {
          setError(RESUMED_BILL_NOT_DRAFT_MSG);
        } else if (err instanceof ApiError && err.statusCode === 409 && /being edited by/i.test(err.message)) {
          // Another terminal holds (or took over) the draft-edit lock.
          setError(err.message);
        } else if (err instanceof ApiError && err.message === 'PRICE_CHANGED') {
          // The catalogue price moved since these lines were added. Show the cart
          // at the new price and let the cashier confirm it with the customer.
          const changes = priceChangesOf(err);
          repriceLines(changes.map((c) => ({ productId: c.product_id, price: c.current_unit_price })));
          setShowPayment(false);
          setPriceNotice(
            `Price changed since added: ${changes
              .map(
                (c) =>
                  `${c.product_name} ${formatCurrency(c.expected_unit_price, tenantCurrency)} → ${formatCurrency(c.current_unit_price, tenantCurrency)}`,
              )
              .join(', ')}. The cart now shows the new price — confirm it with the customer and pay again, or remove the item.`,
          );
        } else if (err instanceof ApiError && err.statusCode === 403 && /discount directly/i.test(err.message)) {
          // SS3-007: the bill discount is over this role's limit. Reopen the
          // discount step, which offers Request Approval, instead of the raw API text.
          setShowPayment(false);
          setShowBillDiscountModal(true);
          setError('This discount is over your limit. Lower it, or tap Request Approval.');
        } else if (err instanceof ApiError && TENDER_REFUSAL_MSG[err.message]) {
          setError(TENDER_REFUSAL_MSG[err.message]);
        } else if (err instanceof ApiError) {
          // 'Validation failed' alone tells the cashier nothing — the per-field
          // reasons live in details[]. Surface them so a rejected tender is
          // actionable at the counter instead of a generic red banner.
          const fieldReasons = err.details?.map((d) => d.message).join('; ');
          setError(fieldReasons ? `${err.message}: ${fieldReasons}` : err.message);
        } else if (err instanceof Error && err.message) {
          // Client-side guards (e.g. an unsupported split tender) already carry
          // a cashier-facing message — don't bury it under the generic fallback.
          setError(err.message);
        } else {
          setError('Payment processing failed. Please try again.');
        }
      } finally {
        setIsProcessing(false);
        setTerminalWait(null);
        terminalAbortRef.current = null;
        // #308: stock was deducted (success) or a shortage was reported
        // (failure) — refresh recipe availability so the grid tiles and the
        // add-to-cart guard reflect the latest ingredient stock.
        refreshRecipeAvailability();
        // Tile stock counts come from the product list — re-fetch so the
        // grid reflects the deduction instead of the pre-sale snapshot.
        refreshProducts();
      }
    },
    [items, selectedCustomer, totals, payableTotal, clearCart, isConnected, profile, billDiscount, currentBillId, refreshRecipeAvailability, refreshProducts, repriceLines, tenantCurrency, creditAccount, finishOnlineSale, finishOfflineSale, pendingCreditApproval, billingQuote, expiredBatchAccepted],
  );

  /** T19: the cashier has the card slip / UPI success screen — save the sale offline. */
  const handleOfflineSlipConfirm = useCallback(() => {
    const pending = offlineSlip;
    if (!pending) return;
    setOfflineSlip(null);
    offlineSlipConfirmedRef.current = { reference: offlineSlipReference.trim() };
    void handlePaymentConfirm(pending.method, pending.splits, undefined, undefined, pending.cashTendered);
  }, [offlineSlip, offlineSlipReference, handlePaymentConfirm]);

  /** A draft this checkout created is voided when the sale is given up. */
  const voidDraftQuietly = useCallback((id: string | null) => {
    if (!id) return;
    void (async () => {
      try {
        await billingService.voidBill(id);
      } catch {
        /* leave the draft; it stays visible in Bill History */
      }
    })();
  }, []);

  /** T08: the manager approved the sale over the credit limit — complete the same bill. */
  const handleCreditApproved = useCallback(
    async (grant: ManagerApprovalGrant) => {
      const pending = pendingCreditApproval;
      if (!pending) return;
      setPendingCreditApproval(null);
      setIsProcessing(true);
      setError(null);
      try {
        const completed = await billingService.completeBill(pending.billId, {
          ...pending.payload,
          approval_token: grant.approval_token,
        });
        finishOnlineSale(completed, pending.method);
        hideSuccessAfter(4000);
      } catch (err) {
        voidDraftQuietly(pending.draftId);
        setError(err instanceof Error && err.message ? err.message : 'Payment processing failed. Please try again.');
      } finally {
        setIsProcessing(false);
        refreshRecipeAvailability();
        refreshProducts();
      }
    },
    [pendingCreditApproval, finishOnlineSale, hideSuccessAfter, voidDraftQuietly, refreshRecipeAvailability, refreshProducts],
  );

  const handleCreditApprovalCancel = useCallback(() => {
    if (pendingCreditApproval) voidDraftQuietly(pendingCreditApproval.draftId);
    setPendingCreditApproval(null);
    setError("Not completed — this sale is over the customer's credit limit. Ask a manager, or choose another payment method.");
  }, [pendingCreditApproval, voidDraftQuietly]);

  // Another terminal (MANAGER+ takeover) claimed the draft this terminal was
  // editing — warn immediately; the next PATCH/complete would 409 anyway.
  useEffect(() => {
    if (!currentBillId) return;
    return onSocketEvent<{ bill_id: string; action: string; by_user_name: string }>('bill:lock', (p) => {
      if (p.bill_id === currentBillId && p.action === 'taken') {
        toast({ title: `This bill was taken over by ${p.by_user_name} on another terminal.`, variant: 'error' });
      }
    });
  }, [currentBillId, toast]);

  // gst-parked 120: online the bill parks on the server, in the store's list on
  // every till; offline it parks on this till and uploads when back online.
  // A refusal from the server keeps the cart and says why.
  const handleParkBill = useCallback(async () => {
    if (items.length === 0 || isParking) return;
    setIsParking(true);
    try {
      const mode = billMode;
      const outcome = await parkCurrentBill(
        promoPreview.key === promoKey ? promoPreview.byLine : undefined,
      );
      setShowParkPanel(false);
      if (outcome?.where === 'server') {
        toast({
          title: `Bill parked as #${outcome.reference} — any till at this store can resume it.`,
          variant: 'success',
        });
      } else if (outcome) {
        toast({
          title:
            mode === 'sale'
              ? `Bill parked on this till as #${outcome.reference} — it uploads when the connection is back.`
              : `Bill parked on this till as #${outcome.reference}.`,
          variant: 'info',
        });
      }
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'Could not park this bill. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsParking(false);
    }
  }, [items.length, isParking, billMode, parkCurrentBill, toast, promoPreview, promoKey]);

  const handleResumeBill = useCallback(
    async (billId: string) => {
      const entry = parkedBills.find((b) => b.id === billId);
      setShowParkPanel(false);
      if (entry && !entry.localState) {
        if (!isConnected) {
          toast({ title: 'Reconnect to resume this bill — it is parked on the server.', variant: 'error' });
          return;
        }
        await resumeServerBill(billId, { parkedOnly: true });
        return;
      }
      const autoParked = await resumeBill(billId);
      if (autoParked) {
        toast({
          title: `Your current bill was parked as #${autoParked}`,
          variant: 'info',
        });
      }
    },
    [parkedBills, isConnected, resumeServerBill, resumeBill, toast],
  );

  const handleDiscardParked = useCallback(
    async (billId: string) => {
      const parked = parkedBills.find((b) => b.id === billId);
      const label = parked ? `#${parked.referenceNumber}` : 'this parked bill';
      const everywhere = parked && !parked.localState ? ' It goes from every till at this store.' : '';
      if (!window.confirm(`Discard ${label}? Its items cannot be recovered.${everywhere}`)) return;
      try {
        await discardParkedBill(billId);
      } catch (err) {
        toast({
          title: err instanceof Error ? err.message : 'Could not discard this parked bill. Please try again.',
          variant: 'error',
        });
      }
    },
    [discardParkedBill, parkedBills, toast],
  );

  // ── F8: bill-level discount handlers ────────────────────────────────────
  // C03: the scope of the voucher last checked in the modal, attached when
  // that voucher is applied (a voucher for some items only).
  const voucherScopeRef = useRef<{ code: string; eligibleProductIds: string[]; scopeLabel: string } | null>(null);
  const handleApplyBillDiscount = useCallback(
    (d: { type: 'PERCENTAGE' | 'FLAT'; value: number; reason: string; voucherCode?: string }) => {
      const scope = d.voucherCode && voucherScopeRef.current?.code === d.voucherCode ? voucherScopeRef.current : null;
      try {
        applyBillDiscount({
          type: d.type,
          value: d.value,
          reason: d.reason,
          appliedBy: profile?.id ?? 'unknown',
          ...(d.voucherCode ? { voucherCode: d.voucherCode } : {}),
          ...(scope ? { eligibleProductIds: scope.eligibleProductIds, scopeLabel: scope.scopeLabel } : {}),
        });
        setShowBillDiscountModal(false);
        toast({
          title: d.voucherCode ? `Voucher ${d.voucherCode} applied.` : 'Bill discount applied.',
          variant: 'success',
        });
      } catch (err) {
        toast({
          title: err instanceof Error ? err.message : 'Failed to apply discount.',
          variant: 'error',
        });
      }
    },
    [applyBillDiscount, profile, toast],
  );

  // Voucher validate hook for the modal — POS calls /vouchers/validate and
  // surfaces a server-computed preview without touching the live bill yet.
  // The actual redemption (which writes used_count + the redemption row)
  // happens at checkout, after we have a draft bill id.
  const handleValidateVoucher = useCallback(
    async (code: string) => {
      if (!currentLocationId) {
        throw new Error('No active location — select a location first.');
      }
      const billAmount = totals.subtotal;
      if (billAmount <= 0) {
        throw new Error('Add at least one item to the cart first.');
      }
      const result = await vouchersService.validate({
        code,
        bill_amount: billAmount,
        location_id: currentLocationId,
        ...(selectedCustomer?.id ? { customer_id: selectedCustomer.id } : {}),
        // The priced lines (automatic promotion included), so the amount
        // matches what redeem takes off the bill.
        lines: voucherLinesOf(pricedItems),
      });
      voucherScopeRef.current = result.scoped
        ? { code, eligibleProductIds: result.eligible_product_ids ?? [], scopeLabel: result.scope_label ?? 'matching' }
        : null;
      return {
        type: result.type,
        discount_amount: result.discount_amount,
        message: result.message,
      };
    },
    [currentLocationId, totals.subtotal, selectedCustomer, pricedItems],
  );

  // C03 D13: a voucher for some items is checked again when the bill's
  // products change; with no matching item left it comes off the bill.
  useScopedVoucherRecheck({
    billDiscount,
    items: pricedItems,
    subtotal: totals.subtotal,
    locationId: currentLocationId,
    customerId: selectedCustomer?.id,
    applyBillDiscount,
    clearBillDiscount,
    onDropped: (message) => toast({ title: message, variant: 'info' }),
  });

  const handleClearBillDiscount = useCallback(() => {
    clearBillDiscount();
    setShowBillDiscountModal(false);
    toast({ title: 'Bill discount removed.', variant: 'info' });
  }, [clearBillDiscount, toast]);

  // ── FR-BILL-050: request discount approval for over-cap discounts ──────
  const handleRequestDiscountApproval = useCallback(
    async (dto: { type: 'PERCENTAGE' | 'FLAT'; value: number; reason: string }) => {
      // An in-progress cart has no server bill until payment, so requiring one
      // here meant an over-cap cashier was simply blocked — the approval route
      // existed but was unreachable on the ordinary path. Create the draft now,
      // exactly as checkout would, so there is something to attach the request
      // to.
      let billId = currentBillId;
      if (!billId) {
        if (!currentLocationId) {
          toast({ title: 'Select a location before requesting discount approval.', variant: 'warning' });
          return;
        }
        try {
          const draft = await billingService.createBill({
            location_id: currentLocationId,
            items: items.map(toBillItem),
            customer_id: selectedCustomer?.id,
          });
          billId = draft.id;
          setCartBillId(draft.id);
        } catch (err) {
          toast({
            title: err instanceof Error ? err.message : 'Could not start a bill for approval.',
            variant: 'error',
          });
          throw err;
        }
      }
      try {
        // The server's wire contract is BASIS POINTS for a percentage, the same
        // as the plain-apply path below. This used to send the modal's whole
        // percent verbatim, so a requested 15% reached the approver as
        // "0.15% discount" and applied 15 bp — and then 403'd at checkout when
        // the real 1500 was re-posted past the cap the approval had bypassed.
        const { request_id } = await billingService.requestDiscountApproval(billId, {
          ...dto,
          value: dto.type === 'PERCENTAGE' ? Math.round(dto.value * 100) : dto.value,
        });
        setPendingDiscountApproval({
          requestId: request_id,
          type: dto.type,
          value: dto.value,
          reason: dto.reason,
          requestedAt: Date.now(),
        });
        setShowBillDiscountModal(false);
        toast({ title: 'Discount approval requested. A Manager/Owner will review.', variant: 'info' });
      } catch (err) {
        toast({
          title: err instanceof Error ? err.message : 'Failed to request discount approval.',
          variant: 'error',
        });
        throw err;
      }
    },
    [currentBillId, currentLocationId, items, selectedCustomer, setCartBillId, setPendingDiscountApproval, toast],
  );

  // ── F12: void current bill ──────────────────────────────────────────────
  const handleConfirmVoid = useCallback(async () => {
    setIsProcessing(true);
    try {
      // If the cart is linked to a server-side DRAFT bill (resumed from a
      // parked bill that was already persisted server-side), void it on
      // the backend too. In-progress local carts have no currentBillId
      // until payment, so this is the uncommon path.
      if (currentBillId) {
        try {
          await billingService.voidBill(currentBillId, voidReason);
        } catch (err) {
          if (err instanceof ApiError) {
            toast({ title: err.message, variant: 'error' });
            return;
          }
          throw err;
        }
      }
      clearCart();
      setShowVoidConfirm(false);
      setMobileView('products');
      toast({ title: 'Bill voided.', variant: 'info' });
    } finally {
      setIsProcessing(false);
    }
  }, [currentBillId, clearCart, toast, voidReason]);

  const handleCategoryClick = useCallback(
    (cat: string | undefined) => {
      setActiveCategory(cat);
      filterByCategory(cat);
    },
    [filterByCategory],
  );

  // ── Cart items mapped for CartSidebar ────────────────────────────────────
  const cartSidebarItems = pricedItems.map((item, idx) => {
    const billShare = totals.perLineBillDiscount[idx] ?? 0;
    return {
      id: item.id,
      // Show chosen add-ons inline; their price is already folded into unitPrice.
      name: item.modifiers ? `${item.name} (${item.modifiers})` : item.name,
      sku: item.sku,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      discountAmount: item.discountAmount,
      billDiscountShare: billShare,
      taxAmount: item.taxAmount,
      // Subtract the pro-rated bill-discount share so the per-line total in
      // the cart row matches the bill's grand total breakdown.
      lineTotal: Math.max(0, item.lineTotal - billShare),
      unit: item.unit,
      decimalQuantity: isMeasureUnit(item.unit),
      warning: expiredBatchWarning(expiredBatchTakes.get(item.productId)),
    };
  });

  // ── Render ───────────────────────────────────────────────────────────────

  // Block interactions until cart is restored from IndexedDB
  if (!isCartHydrated) {
    return (
      <div className="flex items-center justify-center h-full bg-neutral-50 dark:bg-neutral-900">
        <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading POS...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900 overflow-hidden">
      {/* ═══ Network Status Banner ═══ */}
      <NetworkStatusBanner />

      {/* ═══ GST setup warning (India only; dismissible per shift) ═══ */}
      <GstSetupTillBanner />

      {/* ═══ Top Bar ═══ */}
      <header className="shrink-0 flex items-center gap-4 px-6 h-14 bg-gradient-to-r from-white via-brand-50/60 to-indigo-50/60 dark:from-neutral-800 dark:via-neutral-800 dark:to-indigo-950/40 border-b-2 border-brand-500/70 dark:border-brand-500/40 shadow-sm animate-fade-in">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-8 h-8 rounded-md bg-gradient-to-br from-brand-600 to-indigo-600 shadow-md">
            <Logo variant="mark" tone="white" size={18} />
          </div>
          <span className="hidden sm:inline text-sm font-semibold text-neutral-800 dark:text-neutral-100">
            POS Terminal
          </span>
        </div>

        {/* Scanner / Search */}
        <div ref={scannerRef} className="flex-1 max-w-md mx-auto">
          <QuickAddScanner
            onScan={handleHardwareScan}
            onManualAdd={handleManualSearch}
            value={searchText}
            onValueChange={handleSearchTextChange}
            onCameraToggle={() => setShowCameraScanner((prev) => !prev)}
            isCameraOpen={showCameraScanner}
            cameraSupported={cameraSupported}
            placeholder="Scan barcode or search products — press /"
          />
        </div>

        {/* Shift indicator */}
        <ShiftStatusBadge />

        {/* Active role pill — explains why permission-gated buttons appear/hide */}
        <span
          className={[
            'hidden md:inline-flex items-center h-6 px-2.5 rounded-full border',
            'text-[11px] font-semibold tracking-wide tabular-nums uppercase',
            roleBadgeStyle(normalizedRole),
          ].join(' ')}
          aria-label={`Signed in as ${normalizedRole || 'guest'}`}
          title={`Signed in as ${normalizedRole || 'guest'}`}
        >
          {normalizedRole || 'GUEST'}
        </span>

        {/* Notifications */}
        <NotificationBell />

        {/* Quick actions */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Dine-in entry point — RESTAURANT vertical only */}
          {profile?.tenant?.business_type === 'RESTAURANT' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/pos/tables')}
              className={moduleButtonClass('/pos/tables')}
            >
              Tables
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pos/products')}
            className={moduleButtonClass('/pos/products')}
          >
            Catalog
          </Button>
          {/* Quick-Add: MANAGER+ only (PAGES.md §2.29) */}
          {(ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.MANAGER && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/pos/inventory/quick-add')}
              className={moduleButtonClass('/pos/inventory/quick-add')}
            >
              Quick-Add
            </Button>
          )}
          {/* Stock transfers: MANAGER+ only, matching the route guard (PAGES.md §2.23) */}
          {(ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.MANAGER && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/pos/stock-transfer')}
              className={moduleButtonClass(
                '/pos/stock-transfer',
                hasPlanFeature('stock_transfers') ? undefined : lockedButtonClass,
              )}
            >
              Transfers
              {!hasPlanFeature('stock_transfers') && <span className="ml-1"><LockMark /></span>}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pos/bills')}
            className={moduleButtonClass('/pos/bills')}
          >
            Bills
          </Button>
          {parkedBills.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowParkPanel(true)}
            >
              Parked
              <Badge variant="warning" size="sm" className="ml-1.5">
                {parkedBills.length}
              </Badge>
            </Button>
          )}
          {!showParkPanel && (
            <span title={items.length === 0 ? 'Add items to the cart first' : undefined} className="inline-flex">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowParkPanel(true)}
                disabled={items.length === 0}
                aria-label="Park current bill (F4)"
              >
                Park <kbd className="ml-1 px-1 py-0.5 rounded text-[10px] font-mono bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400">F4</kbd>
              </Button>
            </span>
          )}
          <span title={items.length === 0 ? 'Add items to the cart first' : undefined} className="inline-flex">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (items.length === 0) {
                  toast({ title: 'Add items to the cart first.', variant: 'warning' });
                  return;
                }
                setShowBillDiscountModal(true);
              }}
              disabled={items.length === 0}
              aria-label="Apply bill-level discount (F8)"
            >
              Discount <kbd className="ml-1 px-1 py-0.5 rounded text-[10px] font-mono bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400">F8</kbd>
            </Button>
          </span>
          {/* Void: MANAGER+ only (see PAGES.md §2.2 "Override actions are logged for audit") */}
          {(ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.MANAGER && (
            <span title={items.length === 0 ? 'Add items to the cart first' : undefined} className="inline-flex">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (items.length === 0) return;
                  setShowVoidConfirm(true);
                }}
                disabled={items.length === 0}
                aria-label="Void current bill (F12)"
                className="text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300"
              >
                Void <kbd className="ml-1 px-1 py-0.5 rounded text-[10px] font-mono bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400">F12</kbd>
              </Button>
            </span>
          )}
          {/* The online order queue. Reachable from the till's home screen
              because that is where the person who knows what is on the shelf
              is standing — the ORDER_NEW notification is the prompt, this is
              the way back to the list afterwards. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pos/online-orders')}
            aria-label={
              hasPlanFeature('online_store')
                ? 'Online orders from your website'
                : 'Online orders from your website (not in your plan)'
            }
            className={moduleButtonClass(
              '/pos/online-orders',
              hasPlanFeature('online_store') ? undefined : lockedButtonClass,
            )}
          >
            Online
            {!hasPlanFeature('online_store') && <span className="ml-1"><LockMark /></span>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pos/settings')}
            aria-label="Settings"
            className={moduleButtonClass('/pos/settings')}
          >
            Settings
          </Button>
        </div>
      </header>

      {/* ═══ Success / Error banners ═══ */}
      {error && (
        <div className="px-6 pt-2 shrink-0">
          <Alert
            variant="danger"
            description={error}
            dismissible
            onDismiss={() => setError(null)}
          />
        </div>
      )}
      {billingQuote && (
        <div className="px-6 pt-2 shrink-0 animate-fade-in">
          <Alert
            variant="info"
            description={
              quoteAdvance > 0
                ? `Billing quote ${billingQuote.quoteNumber}. Down payment of ${formatCurrency(quoteAdvance, tenantCurrency)} is deducted — collect ${formatCurrency(Math.max(0, payableTotal), tenantCurrency)}.`
                : `Billing quote ${billingQuote.quoteNumber}. Paying closes the quote and links it to this bill.`
            }
          />
        </div>
      )}
      {priceNotice && (
        <div className="px-6 pt-2 shrink-0">
          <Alert
            variant="warning"
            description={priceNotice}
            dismissible
            onDismiss={() => setPriceNotice(null)}
          />
        </div>
      )}
      {successMsg && (
        <div className="px-6 pt-2 shrink-0">
          <Alert
            variant="success"
            description={successMsg}
            // A created quote must be reachable: the cart has just cleared, so
            // without this the only trace of it is the number in this text.
            action={createdQuoteId ? {
              label: 'Open quote',
              onClick: () => navigate(`/pos/quotations/${createdQuoteId}`),
            } : undefined}
            dismissible
            onDismiss={() => { setSuccessMsg(null); setCreatedQuoteId(null); }}
          />
        </div>
      )}

      {/* ═══ Main Content — Split Layout ═══ */}
      <div
        className={[
          'flex flex-1 overflow-hidden',
          // LEFT flips the row so the cart sits on the screen-left edge
          // without duplicating any JSX. Mobile (single-column) is unaffected.
          cartPanelPosition === 'LEFT' ? 'lg:flex-row-reverse' : '',
        ].join(' ')}
        data-cart-panel-position={cartPanelPosition}
      >
        {/* ─── Left: Products (hidden on mobile when cart active) ─── */}
        <main
          className={[
            'flex flex-col flex-1 overflow-hidden bg-neutral-50 dark:bg-neutral-900',
            mobileView === 'cart' ? 'hidden lg:flex' : 'flex',
          ].join(' ')}
        >
          {/* Daypart selector — only when dayparts are configured for this location */}
          {periods.length > 0 && (
            <div className="shrink-0 px-6 pt-3 bg-white dark:bg-neutral-800 overflow-x-auto scrollbar-none">
              <Tabs
                variant="pill"
                scrollable
                activeId={selectedDaypartId}
                onChange={(id) => setSelectedDaypartId(String(id))}
                items={[
                  { id: '__all', label: 'All Day' },
                  ...periods.map((p) => ({ id: p.id, label: p.name })),
                ]}
              />
            </div>
          )}

          {/* Category tabs — @bbpos/ui Tabs in pill variant */}
          {categories.length > 0 && (
            <div className="shrink-0 px-6 py-3 bg-white dark:bg-neutral-800 overflow-x-auto scrollbar-none">
              <Tabs
                variant="pill"
                scrollable
                activeId={activeCategory ?? '__all'}
                onChange={(id) => handleCategoryClick(id === '__all' ? undefined : id)}
                items={[
                  { id: '__all', label: 'All' },
                  ...categories.map((cat) => ({ id: cat, label: cat })),
                ]}
              />
            </div>
          )}

          {/* Product grid */}
          <div className="flex-1 overflow-y-auto p-6 pr-4">
            <ProductGrid
              onLoadVariants={loadVariants}
              products={gridWithAvailability}
              onSelect={handleProductSelect}
              isLoading={productsLoading}
              currency={tenantCurrency}
              taxInclusive={taxInclusivePricing && !noGst}
              sellOutOfStock={!isRestaurantTenant && billMode === 'sale'}
            />
          </div>
        </main>

        {/* ─── Right: Cart Sidebar (hidden on mobile when products active) ─── */}
        <aside
          className={[
            'w-full lg:w-cart-sidebar lg:max-w-cart-sidebar md:w-cart-sidebar-md shrink-0',
            'bg-white dark:bg-neutral-800 z-dropdown',
            'shadow-[inset_1px_0_0_0_rgb(0_0_0/0.04)] dark:shadow-[inset_1px_0_0_0_rgb(255_255_255/0.06)]',
            mobileView === 'products' ? 'hidden lg:flex' : 'flex',
          ].join(' ')}
        >
          <div className="flex flex-col w-full h-full min-h-0">
          {currentBillId && (
            <div
              role="status"
              className="shrink-0 flex items-center justify-between gap-2 px-4 py-2 text-xs bg-gradient-to-r from-brand-50 to-indigo-50 dark:from-brand-900/30 dark:to-indigo-900/30 border-b border-brand-100 dark:border-brand-800 text-brand-800 dark:text-brand-200 animate-fade-in"
            >
              <span>
                Resumed draft — paying completes the <span className="font-semibold">same</span> bill
              </span>
              <button
                type="button"
                onClick={() => setShowVoidConfirm(true)}
                className="font-semibold text-danger-600 hover:text-danger-700 dark:text-danger-400 transition-colors duration-200"
              >
                Discard draft
              </button>
            </div>
          )}
          <CartSidebar
            className="flex-1 min-h-0"
            items={cartSidebarItems}
            highlightItemId={lastAddedItemId}
            onQuantityChange={handleQuantityChange}
            onDiscountChange={handleDiscountChange}
            onRemove={handleRemoveItem}
            selectedCustomer={selectedCustomer ?? undefined}
            customer360={customer360 ?? undefined}
            onCustomerSelect={handleCustomerSelect}
            onCustomerClear={handleCustomerClear}
            onCustomerSearch={handleCustomerSearch}
            billMode={billMode}
            onBillModeChange={handleBillModeChange}
            subtotal={totals.subtotal}
            discountAmount={totals.totalDiscount}
            billDiscountAmount={totals.totalBillDiscount}
            cgst={totals.cgst}
            sgst={totals.sgst}
            igst={totals.igst}
            taxSlabs={totals.taxSlabs}
            roundOff={totals.roundOff}
            total={totals.grandTotal}
            exchangeCredit={
              exchangeApplied > 0 && exchangeCredit
                ? { amount: exchangeApplied, creditNoteNumber: exchangeCredit.creditNoteNumber }
                : undefined
            }
            quoteAdvance={
              quoteAdvance > 0 && billingQuote
                ? { amount: quoteAdvance, quoteNumber: billingQuote.quoteNumber }
                : undefined
            }
            pendingDiscountApproval={pendingDiscountApproval}
            onCheckout={handleCheckout}
            currency={tenantCurrency}
            taxInclusive={taxInclusivePricing && !noGst}
            isInterState={isInterState}
            onInterStateChange={tax.splitLabels && !noGst ? handleInterStateChange : undefined}
            interStateWarning={supplySource === 'DEFAULT' ? SUPPLY_DEFAULT_WARNING : undefined}
            taxNote={noGst ? NOT_GST_REGISTERED : undefined}
            storeCreditAvailable={selectedCustomer?.store_credit_balance ?? 0}
            membershipInfo={
              membership
                ? { tier: membership.tier, planName: membership.planName, discountPct: membership.discountPct }
                : undefined
            }
            modePermissions={modePermissions}
            lockedModes={quotesLocked ? ['quotation'] : undefined}
          />
          </div>
        </aside>
      </div>

      {/* ═══ Mobile Bottom Tabs ═══ */}
      <nav
        className="lg:hidden shrink-0 bg-white dark:bg-neutral-800 shadow-[0_-1px_0_0_rgb(0_0_0/0.06)] dark:shadow-[0_-1px_0_0_rgb(255_255_255/0.06)]"
        aria-label="POS view"
      >
        <Tabs
          variant="default"
          fullWidth
          activeId={mobileView}
          onChange={(id) => setMobileView(id as MobileView)}
          items={[
            { id: 'products', label: 'Products' },
            {
              id: 'cart',
              label: 'Cart',
              badge: items.length > 0 ? totals.itemCount : undefined,
            },
          ]}
        />
      </nav>

      {/* ═══ Customer Capture (pre-payment) ═══ */}
      <CustomerCaptureModal
        open={showCustomerCapture}
        title={billMode === 'quotation' ? 'Customer for this quote' : 'Customer for this bill'}
        onClose={() => setShowCustomerCapture(false)}
        onAttached={(customer) => {
          handleCustomerSelect(customer);
          setShowCustomerCapture(false);
          if (billMode === 'quotation') void saveQuote(customer);
          else proceedToPayment();
        }}
        // Quotes require a customer — no walk-in skip.
        onSkipped={
          billMode === 'quotation'
            ? undefined
            : () => {
                setCustomerSkipped(true);
                setShowCustomerCapture(false);
                proceedToPayment();
              }
        }
      />

      {/* ═══ Serial Capture (pre-payment, SERIAL-tracked lines) ═══ */}
      <SerialCaptureModal
        open={showSerialCapture}
        lines={serialLines}
        locationId={currentLocationId ?? undefined}
        onChange={setLineSerials}
        onConfirm={() => {
          setShowSerialCapture(false);
          setShowPayment(true);
        }}
        onClose={() => setShowSerialCapture(false)}
      />

      {/* ═══ Card / UPI terminal wait ═══ */}
      {terminalWait && (
        <TerminalPaymentDialog
          terminalName={terminalWait.terminalName}
          amount={terminalWait.amount}
          currency={tenantCurrency}
          status={terminalWait.status}
          transactionReference={terminalWait.transactionReference}
          onCancel={() => terminalAbortRef.current?.abort()}
        />
      )}

      {/* ═══ Payment Modal ═══ */}
      <PaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        totalAmount={payableTotal}
        cashRoundOff={totals.roundOff}
        onConfirm={handlePaymentConfirm}
        isProcessing={isProcessing}
        confirmTriggerRef={paymentConfirmTriggerRef}
        currency={tenantCurrency}
        defaultMethod={defaultPaymentMethod}
        storeCreditAvailable={selectedCustomer?.store_credit_balance ?? 0}
        loyalty={loyaltyContext}
        customerLinked={Boolean(selectedCustomer)}
        accountCredit={
          isConnected && creditAccount
            ? { creditLimit: creditAccount.credit_limit, outstanding: creditAccount.outstanding_balance }
            : undefined
        }
        walletBalance={
          loyaltyBalance
            ? { points: loyaltyBalance.loyalty_points, valueMinor: loyaltyBalance.value_paise }
            : undefined
        }
        onCreditNoteLookup={async (code) => {
          // Bubble backend error messages to the sub-modal so the cashier sees
          // the real reason — Conflict codes like CREDIT_NOTE_EXPIRED become
          // sentences (CU2-008).
          try {
            return await billingService.lookupCreditNote(code);
          } catch (err) {
            const msg =
              err instanceof Error && err.message
                ? creditNoteLookupMessage(err.message)
                : 'Credit note lookup failed';
            throw new Error(msg);
          }
        }}
      />

      {/* ═══ Manager approval (above the payment screen): Account Credit over the limit (T08) ═══ */}
      <ManagerApprovalDialog
        open={pendingCreditApproval !== null}
        action="CREDIT_OVER_LIMIT"
        reason={pendingCreditApproval?.reason ?? ''}
        subjectId={pendingCreditApproval?.billId}
        amount={pendingCreditApproval?.amount}
        locationId={currentLocationId ?? undefined}
        onApproved={(grant) => void handleCreditApproved(grant)}
        onCancel={handleCreditApprovalCancel}
      />

      {/* ═══ Manager approval: the bill-level IGST switch without 'Edit taxes' (T09) ═══ */}
      <ManagerApprovalDialog
        open={pendingInterState !== null}
        action="TAX_SWITCH"
        reason={
          pendingInterState
            ? 'Change this bill to inter-state supply (IGST).'
            : 'Change this bill back to CGST + SGST.'
        }
        locationId={currentLocationId ?? undefined}
        onApproved={(grant) => {
          if (pendingInterState !== null) setIsInterState(pendingInterState, grant.approval_token);
          setPendingInterState(null);
        }}
        onCancel={() => setPendingInterState(null)}
      />

      {/* ═══ Quantity Prompt (Terminal Preferences → quick-add quantity OFF) ═══ */}
      {pendingQtyProduct && (
        <Modal
          open
          onClose={() => setPendingQtyProduct(null)}
          title={`Quantity — ${pendingQtyProduct.name}`}
        >
          <div className="space-y-3">
            <NumPad
              value={qtyInput}
              onChange={setQtyInput}
              label="Quantity"
              compact
              allowDecimal={isMeasureUnit(pendingQtyProduct.unit)}
            />
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleQuantityConfirm}
              disabled={!(qtyToAdd > 0)}
            >
              Add to cart
            </Button>
          </div>
        </Modal>
      )}

      {/* ═══ Park Bill Panel ═══ */}
      <ParkBillPanel
        isOpen={showParkPanel}
        onClose={() => setShowParkPanel(false)}
        parkedBills={parkedBills}
        onPark={handleParkBill}
        canPark={items.length > 0 && !isParking}
        onResume={handleResumeBill}
        onDiscard={handleDiscardParked}
        currency={tenantCurrency}
      />

      {/* ═══ Notification Panel ═══ */}
      <NotificationPanel />

      {/* ═══ Bill Discount Modal (F8) ═══ */}
      <BillDiscountModal
        open={showBillDiscountModal}
        // The bill discount applies to the base after line discounts
        // (use-bill-calculations taxableBase), so the preview starts there.
        currentSubtotal={totals.subtotal - totals.totalDiscount}
        lineItems={items}
        currentDiscount={billDiscount ?? null}
        callerRole={normalizedRole}
        pendingApproval={pendingDiscountApproval}
        onApply={handleApplyBillDiscount}
        // Always offered. This used to be gated on currentBillId, which an
        // in-progress cart does not have until payment — so an over-cap cashier
        // on the ordinary path was hard-blocked and never shown the approval
        // route that exists for exactly this case. The handler creates the draft
        // when there is not one yet.
        onRequestApproval={handleRequestDiscountApproval}
        onValidateVoucher={handleValidateVoucher}
        onClear={handleClearBillDiscount}
        onClose={() => setShowBillDiscountModal(false)}
        currency={tenantCurrency}
      />

      {/* ═══ Shortcut Help Overlay (?) ═══ */}
      <ShortcutHelpOverlay
        open={showShortcutHelp}
        onClose={() => setShowShortcutHelp(false)}
      />

      {/* ═══ Add-on (Modifier) panel — surcharge folded into unit price ═══ */}
      <Modal
        open={pendingModifier !== null}
        onClose={handleCancelModifiers}
        title={pendingModifier ? `Add-ons — ${pendingModifier.productName}` : 'Add-ons'}
        footer={
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-neutral-600 dark:text-neutral-400">
              Add-ons:{' '}
              <span className="font-semibold text-brand-600 dark:text-brand-400">
                {formatCurrency(addOnTotal, tenantCurrency)}
              </span>
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleCancelModifiers}>
                Skip
              </Button>
              <Button variant="primary" size="sm" onClick={handleConfirmModifiers}>
                Apply Add-ons
              </Button>
            </div>
          </div>
        }
      >
        {pendingModifier && (
          <div className="space-y-4">
            {pendingModifier.groups.map((group) => {
              const selected = selectedModifiers[group.id] ?? [];
              return (
                <div key={group.id} className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {group.name}
                  </p>
                  {group.options.map((opt) => {
                    const checked = selected.includes(opt.id);
                    return (
                      <div key={opt.id} className="flex items-center justify-between gap-3">
                        <label className="flex items-center gap-2 text-sm text-neutral-800 dark:text-neutral-100">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setSelectedModifiers((prev) => {
                                const cur = prev[group.id] ?? [];
                                const next = cur.includes(opt.id)
                                  ? cur.filter((id) => id !== opt.id)
                                  : group.multiSelect
                                    ? [...cur, opt.id]
                                    : [opt.id];
                                return { ...prev, [group.id]: next };
                              });
                            }}
                          />
                          {opt.name}
                        </label>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-neutral-400">{getCurrencySymbol(tenantCurrency)}</span>
                          <input
                            type="number"
                            min={0}
                            disabled={!checked}
                            value={(editedPrices[opt.id] ?? opt.additionalPrice) / getSubunitMultiplier(tenantCurrency)}
                            onChange={(e) => {
                              const rupees = parseFloat(e.target.value);
                              setEditedPrices((prev) => ({
                                ...prev,
                                [opt.id]: Number.isFinite(rupees) ? Math.round(rupees * getSubunitMultiplier(tenantCurrency)) : 0,
                              }));
                            }}
                            className="w-20 rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-2 py-1 text-sm text-right disabled:opacity-50"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* ═══ Receipt shown on every completed sale ═══ */}
      {/* ═══ T19: offline card / UPI — the cashier confirms the slip ═══ */}
      <ConfirmDialog
        open={offlineSlip !== null}
        title={`Paid by ${offlineSlip?.methods.map((m) => (m === 'card' ? 'card' : 'UPI')).join(' and ') ?? 'card'} while offline?`}
        description={
          offlineSlip
            ? `Confirm you have the approved card slip or the customer's UPI success screen for ${formatCurrency(offlineSlip.amount, tenantCurrency)}. The till cannot check it offline, so the bill is marked "paid offline — unverified" until a manager checks it.`
            : ''
        }
        confirmLabel="Slip checked — save bill"
        cancelLabel="Back"
        variant="warning"
        onConfirm={handleOfflineSlipConfirm}
        onCancel={() => setOfflineSlip(null)}
      >
        <input
          type="text"
          value={offlineSlipReference}
          onChange={(e) => setOfflineSlipReference(e.target.value)}
          maxLength={100}
          aria-label="Slip or UPI reference"
          placeholder="Slip / approval / UPI reference (optional)"
          className="mt-3 w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </ConfirmDialog>

      {/* ═══ QA IS4-004: units from an expired batch — warn but allow (ID13) ═══ */}
      <ConfirmDialog
        open={showExpiredBatchConfirm}
        title="Sell units from an expired batch?"
        description={[...expiredBatchTakes.entries()]
          .map(([productId, takes]) => {
            const name = items.find((i) => i.productId === productId)?.name ?? 'Item';
            return `${name}: ${expiredBatchWarning(takes)}`;
          })
          .join('. ')}
        confirmLabel="Sell anyway"
        cancelLabel="Back"
        variant="warning"
        onConfirm={() => {
          setShowExpiredBatchConfirm(false);
          setExpiredBatchAcceptedKey(expiredBatchAcceptKey);
          setShowPayment(true);
        }}
        onCancel={() => setShowExpiredBatchConfirm(false)}
      />

      {/* ═══ QA IS1-005: sell an item that is out of stock ═══ */}
      <ConfirmDialog
        open={outOfStockAdd !== null}
        title={`${outOfStockAdd?.product.name ?? 'This item'} is out of stock`}
        description={
          outOfStockAdd
            ? `Stock shows ${Number(outOfStockAdd.product.stock_quantity)}${
                Number(outOfStockAdd.product.held_quantity ?? 0) > 0
                  ? ` (${Number(outOfStockAdd.product.held_quantity)} held for wastage approval)`
                  : ''
              }${
                outOfStockAdd.inCart > 0 ? ` and ${outOfStockAdd.inCart} already in the cart` : ''
              }. Sell it anyway? Stock will go below zero until it is restocked or counted.`
            : ''
        }
        confirmLabel="Sell anyway"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={() => {
          const pending = outOfStockAdd;
          setOutOfStockAdd(null);
          pending?.proceed();
        }}
        onCancel={() => setOutOfStockAdd(null)}
      />

      {receiptBill && (
        <PostSaleReceiptModal
          bill={receiptBill}
          offline={receiptOffline}
          onClose={() => {
            setReceiptBill(null);
            // Promote the queued re-order QR now the receipt is out of the way.
            if (pendingReorderBill) {
              setReorderModalBill(pendingReorderBill);
              setPendingReorderBill(null);
            }
          }}
        />
      )}

      {/* ═══ Re-order QR modal (G4.3) ═══ */}
      {reorderModalBill && (
        <ReorderQRModal
          billId={reorderModalBill.billId}
          billNumber={reorderModalBill.billNumber}
          defaultPhone={reorderModalBill.phone}
          defaultEmail={reorderModalBill.email}
          onClose={() => setReorderModalBill(null)}
        />
      )}

      {/* ═══ Void Bill Confirm (F12) ═══ */}
      <ConfirmDialog
        open={showVoidConfirm}
        title="Void current bill?"
        description="This will discard all items in the cart. This action cannot be undone."
        confirmLabel="Void Bill"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmVoid}
        onCancel={() => setShowVoidConfirm(false)}
      >
        {/* Only a server draft has a bill to keep the reason on. */}
        {currentBillId && (
          <input
            type="text"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            maxLength={500}
            aria-label="Void reason"
            placeholder="Reason (optional)"
            className="mt-3 w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-danger-500"
          />
        )}
      </ConfirmDialog>

      {/* ═══ Camera Barcode Scanner (F2) ═══ */}
      <CameraBarcodeScanner
        open={showCameraScanner}
        onScan={(barcode) => {
          handleBarcodeScan(barcode);
          if (!scannerPrefs.continuousScanMode) setShowCameraScanner(false);
        }}
        onClose={() => setShowCameraScanner(false)}
        soundEnabled={scannerPrefs.soundOnScan}
        preferredCameraId={scannerPrefs.preferredCameraId || undefined}
        continuousMode={scannerPrefs.continuousScanMode}
        onImageMatch={(product) => {
          void handleImageMatchPick(product);
          if (!scannerPrefs.continuousScanMode) setShowCameraScanner(false);
        }}
        onImageMatchAddAsNew={() => {
          // Empty-state escape hatch — navigate MANAGER+ users to Quick-Add
          // so they can capture the unknown SKU. QuickAddProductPage picks
          // up the current location from its own store, so we don't need
          // to pass a query parameter here.
          navigate('/pos/inventory/quick-add');
        }}
        locationId={currentLocationId ?? undefined}
      />
    </div>
  );
}
